import React, { useState } from 'react';
import { RfeNoidResponseAgentFlow as RfeFlow } from './RfeAgentFlow';
import { AsylumStep4EvidenceCollection, AsylumStep5CountryAnalysis } from './AsylumSteps';
import { DacaStep1CategorySelection, DacaStep2EligibilityCheck, DacaStep3DocumentUpload, DacaStep4EvidenceAnalysis, DacaStep5AffidavitBuilder } from './DacaSteps';
import { DacaStep6PacketPreparation, DacaStep7GeneratePacket, DacaStep8FinalReminders } from './DacaSteps6to8';
import { VisitorStep1VisaTypeSelection, VisitorStep2PersonalDetails, VisitorStep3DocumentUpload } from './VisitorTouristSteps';
import { VisitorStep4ApplicationAnalysis, VisitorStep5InterviewPreparation, VisitorStep6GeneratePackage, VisitorStep7FinalGuidance } from './VisitorTouristSteps6to7';
import { CitizenshipStep1EligibilityScreening, CitizenshipStep2BackgroundAssessment, CitizenshipStep3DocumentUpload } from './CitizenshipSteps';
import { CitizenshipStep4TravelHistory, CitizenshipStep5BackgroundReview } from './CitizenshipSteps4to5';
import { CitizenshipStep6DocumentEnhancement, CitizenshipStep7FinalVerification, CitizenshipStep8GeneratePackage } from './CitizenshipSteps6to8';

// Work Visa Agent Flow
export const WorkVisaAgentFlow = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    visaType: '',
    employerDetails: {},
    jobDetails: {},
    employeeDetails: {},
    documents: [],
    analysisResults: {},
    recommendations: [],
    finalScore: 0
  });

  const steps = [
    'Welcome & Visa Type Selection',
    'Profile & Job Overview',
    'Guided Document Upload',
    'Smart Evidence Analysis',
    'Suggest Fixes & Offer Help',
    'Correction Loop',
    'Final Review & Checklist',
    'Generate Output Packet'
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-3xl mr-4">💼</span>
              <div>
                <h2 className="text-2xl font-bold text-white">Work Visa Agent</h2>
                <p className="text-gray-400">Employment-Based Visa Process</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Step {currentStep + 1} of {steps.length}</span>
              <span className="text-sm text-gray-400">{Math.round((currentStep / (steps.length - 1)) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-orange-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
              ></div>
            </div>
            <p className="text-white font-medium mt-2">{steps[currentStep]}</p>
          </div>

          {/* Step Content */}
          {currentStep === 0 && (
            <WorkVisaStep1Welcome 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
            />
          )}
          {currentStep === 1 && (
            <WorkVisaStep2Profile 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 2 && (
            <WorkVisaStep3Documents 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 3 && (
            <WorkVisaStep4Analysis 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 4 && (
            <WorkVisaStep5Recommendations 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 5 && (
            <WorkVisaStep6Corrections 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 6 && (
            <WorkVisaStep7FinalReview 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 7 && (
            <WorkVisaStep8GeneratePacket 
              formData={formData} 
              setFormData={setFormData} 
              onBack={handleBack} 
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Asylum Step 1: Case Type & Intake
const AsylumStep1CaseType = ({ formData, setFormData, onNext }) => {
  const [selectedCaseType, setSelectedCaseType] = useState(formData.caseType || '');
  const [personalDetails, setPersonalDetails] = useState(formData.personalDetails || {});

  const caseTypes = [
    {
      id: 'asylum',
      title: 'Asylum Application',
      description: 'Protection for individuals fleeing persecution in their home country',
      requirements: ['Persecution based on protected ground', 'Applied within 1 year of arrival', 'Physical presence in US'],
      timeframe: 'Must apply within 1 year of arrival (with exceptions)',
      icon: '🏃'
    },
    {
      id: 'withholding',
      title: 'Withholding of Removal',
      description: 'Protection from being returned to country where you face persecution',
      requirements: ['Clear probability of persecution', 'No 1-year filing deadline', 'Higher standard of proof'],
      timeframe: 'No filing deadline',
      icon: '🚫'
    },
    {
      id: 'vawa',
      title: 'VAWA Self-Petition',
      description: 'Protection for victims of domestic violence by US citizen/LPR spouse',
      requirements: ['Abuse by qualifying relative', 'Good moral character', 'Extreme hardship'],
      timeframe: 'No specific deadline',
      icon: '💪'
    },
    {
      id: 't-visa',
      title: 'T-Visa (Trafficking)',
      description: 'Protection for victims of severe human trafficking',
      requirements: ['Victim of severe trafficking', 'Physical presence due to trafficking', 'Cooperation with law enforcement'],
      timeframe: 'No specific deadline for initial application',
      icon: '⛓️'
    },
    {
      id: 'u-visa',
      title: 'U-Visa (Crime Victims)',
      description: 'Protection for victims of certain crimes who assist law enforcement',
      requirements: ['Victim of qualifying crime', 'Suffered substantial harm', 'Law enforcement certification'],
      timeframe: 'Must apply while case is pending or within reasonable time',
      icon: '🚔'
    }
  ];

  const handleCaseTypeSelection = (caseType) => {
    setSelectedCaseType(caseType);
    setFormData({ 
      ...formData, 
      caseType: caseType
    });
  };

  const handlePersonalDetailChange = (field, value) => {
    const updated = { ...personalDetails, [field]: value };
    setPersonalDetails(updated);
    setFormData({ ...formData, personalDetails: updated });
  };

  const handleContinue = () => {
    if (selectedCaseType && personalDetails.age && personalDetails.currentLocation) {
      onNext();
    }
  };

  const isValidForm = () => {
    return selectedCaseType && personalDetails.age && personalDetails.currentLocation;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Case Type & Initial Intake</h3>
        <p className="text-gray-400 text-lg">Select the type of protection case that best describes your situation</p>
      </div>

      {/* Case Type Selection */}
      <div className="space-y-4">
        <h4 className="text-xl font-semibold text-white mb-4">Protection Case Types</h4>
        {caseTypes.map((caseType) => (
          <div
            key={caseType.id}
            onClick={() => handleCaseTypeSelection(caseType.id)}
            className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
              selectedCaseType === caseType.id
                ? 'border-red-500 bg-red-900/20'
                : 'border-gray-700 bg-gray-800 hover:border-red-400'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className={`w-6 h-6 rounded-full border-2 mr-4 flex items-center justify-center ${
                  selectedCaseType === caseType.id ? 'border-red-500 bg-red-500' : 'border-gray-500'
                }`}>
                  {selectedCaseType === caseType.id && <span className="text-white text-sm">✓</span>}
                </div>
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{caseType.icon}</span>
                  <div>
                    <h5 className="text-xl font-semibold text-white">{caseType.title}</h5>
                    <p className="text-gray-300 mt-1">{caseType.description}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="ml-10">
              <div className="mb-3">
                <h6 className="text-red-300 font-medium mb-2">Key Requirements:</h6>
                <ul className="space-y-1">
                  {caseType.requirements.map((req, index) => (
                    <li key={index} className="text-gray-400 text-sm flex items-center">
                      <span className="text-red-400 mr-2">•</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-sm text-yellow-300">
                <strong>Timeline:</strong> {caseType.timeframe}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Basic Information */}
      {selectedCaseType && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-4">📋 Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Age *</label>
              <input
                type="number"
                placeholder="Your current age"
                value={personalDetails.age || ''}
                onChange={(e) => handlePersonalDetailChange('age', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Gender</label>
              <select
                value={personalDetails.gender || ''}
                onChange={(e) => handlePersonalDetailChange('gender', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
              >
                <option value="">Select Gender</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="non-binary">Non-binary</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-300 mb-2">Current Location in US *</label>
              <input
                type="text"
                placeholder="City, State where you currently reside"
                value={personalDetails.currentLocation || ''}
                onChange={(e) => handlePersonalDetailChange('currentLocation', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-red-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Safety Reminder */}
      {selectedCaseType && (
        <div className="bg-red-900/20 border border-red-400 p-4 rounded-lg">
          <h4 className="text-red-300 font-semibold mb-2">🔒 Privacy & Safety</h4>
          <p className="text-red-200 text-sm">
            Your information is handled with the utmost confidentiality. If you're in immediate danger, please contact emergency services or reach out to appropriate crisis hotlines before proceeding.
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-end">
        <button
          onClick={handleContinue}
          disabled={!isValidForm()}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            isValidForm()
              ? 'bg-red-500 text-white hover:bg-red-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Safety Assessment →
        </button>
      </div>
    </div>
  );
};

// Asylum Step 2: Personal Safety Assessment  
const AsylumStep2SafetyAssessment = ({ formData, setFormData, onNext, onBack }) => {
  const [safetyStatus, setSafetyStatus] = useState({
    currentSafety: '',
    immediateThreats: false,
    threatDetails: '',
    persecutorLocation: '',
    needsProtection: false,
    familySafety: '',
    contactWithPersecutors: false
  });

  const [countryDetails, setCountryDetails] = useState({
    countryOfOrigin: formData.countryOfOrigin || '',
    arrivalDate: formData.arrivalDate || '',
    reasonForLeaving: ''
  });

  React.useEffect(() => {
    if (formData.safetyStatus) {
      setSafetyStatus(formData.safetyStatus);
    }
    if (formData.countryDetails) {
      setCountryDetails(formData.countryDetails);
    }
  }, []);

  const handleSafetyChange = (field, value) => {
    const updated = { ...safetyStatus, [field]: value };
    setSafetyStatus(updated);
    setFormData({ ...formData, safetyStatus: updated });
  };

  const handleCountryChange = (field, value) => {
    const updated = { ...countryDetails, [field]: value };
    setCountryDetails(updated);
    setFormData({ ...formData, countryDetails: updated, [field]: value });
  };

  const isValidForm = () => {
    return safetyStatus.currentSafety && 
           countryDetails.countryOfOrigin && 
           countryDetails.arrivalDate && 
           countryDetails.reasonForLeaving;
  };

  const handleContinue = () => {
    if (isValidForm()) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Personal Safety Assessment</h3>
        <p className="text-gray-400 text-lg">Help us understand your current safety situation and background</p>
      </div>

      {/* Current Safety Status */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">🛡️ Current Safety Status</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2">How would you describe your current safety level? *</label>
            <select
              value={safetyStatus.currentSafety}
              onChange={(e) => handleSafetyChange('currentSafety', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
            >
              <option value="">Select safety level</option>
              <option value="safe">I feel safe now</option>
              <option value="somewhat-safe">I feel somewhat safe</option>
              <option value="concerned">I have some safety concerns</option>
              <option value="unsafe">I do not feel safe</option>
              <option value="immediate-danger">I am in immediate danger</option>
            </select>
          </div>

          {safetyStatus.currentSafety === 'immediate-danger' && (
            <div className="bg-red-900/30 border border-red-400 p-4 rounded">
              <h5 className="text-red-300 font-bold mb-2">⚠️ Immediate Safety Alert</h5>
              <p className="text-red-200 mb-3">
                If you are in immediate danger, please contact emergency services immediately:
              </p>
              <ul className="text-red-200 text-sm space-y-1">
                <li>• Emergency Services: 911</li>
                <li>• National Domestic Violence Hotline: 1-800-799-7233</li>
                <li>• National Human Trafficking Hotline: 1-888-373-7888</li>
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Are there any immediate threats to your safety?</label>
              <select
                value={safetyStatus.immediateThreats ? 'yes' : 'no'}
                onChange={(e) => handleSafetyChange('immediateThreats', e.target.value === 'yes')}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
              >
                <option value="">Select...</option>
                <option value="no">No immediate threats</option>
                <option value="yes">Yes, there are threats</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Do you know the location of those who harmed you?</label>
              <select
                value={safetyStatus.persecutorLocation}
                onChange={(e) => handleSafetyChange('persecutorLocation', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
              >
                <option value="">Select...</option>
                <option value="unknown">I don't know their location</option>
                <option value="home-country">They are in my home country</option>
                <option value="us">They are in the United States</option>
                <option value="other-country">They are in another country</option>
              </select>
            </div>
          </div>

          {safetyStatus.immediateThreats && (
            <div>
              <label className="block text-gray-300 mb-2">Please briefly describe any current threats (optional)</label>
              <textarea
                value={safetyStatus.threatDetails}
                onChange={(e) => handleSafetyChange('threatDetails', e.target.value)}
                placeholder="You can provide general information about current concerns..."
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-red-500 h-24"
              />
            </div>
          )}
        </div>
      </div>

      {/* Country and Arrival Information */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">🌍 Background Information</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Country of Origin *</label>
            <input
              type="text"
              placeholder="Country where you are from"
              value={countryDetails.countryOfOrigin}
              onChange={(e) => handleCountryChange('countryOfOrigin', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-red-500"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Date of Arrival in US *</label>
            <input
              type="date"
              value={countryDetails.arrivalDate}
              onChange={(e) => handleCountryChange('arrivalDate', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-gray-300 mb-2">Primary reason for leaving your home country *</label>
          <select
            value={countryDetails.reasonForLeaving}
            onChange={(e) => handleCountryChange('reasonForLeaving', e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
          >
            <option value="">Select primary reason</option>
            <option value="political-persecution">Political persecution</option>
            <option value="religious-persecution">Religious persecution</option>
            <option value="ethnic-persecution">Racial/ethnic persecution</option>
            <option value="gender-persecution">Gender-based persecution</option>
            <option value="sexual-orientation">Persecution based on sexual orientation/gender identity</option>
            <option value="domestic-violence">Domestic violence</option>
            <option value="human-trafficking">Human trafficking</option>
            <option value="gang-violence">Gang violence</option>
            <option value="government-corruption">Government corruption/oppression</option>
            <option value="war-violence">War or armed conflict</option>
            <option value="other">Other persecution</option>
          </select>
        </div>
      </div>

      {/* Family Safety */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">👨‍👩‍👧‍👦 Family Safety Considerations</h4>
        
        <div>
          <label className="block text-gray-300 mb-2">Do you have family members who may also need protection?</label>
          <select
            value={safetyStatus.familySafety}
            onChange={(e) => handleSafetyChange('familySafety', e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
          >
            <option value="">Select...</option>
            <option value="no-family">No family members at risk</option>
            <option value="family-safe">Family members are safe</option>
            <option value="family-at-risk">Family members may be at risk</option>
            <option value="family-with-me">Family members are here with me</option>
            <option value="unsure">I'm not sure about their safety</option>
          </select>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="bg-blue-900/20 border border-blue-400 p-4 rounded-lg">
        <h4 className="text-blue-300 font-semibold mb-2">🔒 Confidentiality Notice</h4>
        <p className="text-blue-200 text-sm">
          All information you provide is kept strictly confidential and is protected by attorney-client privilege principles. 
          This information will only be used to help assess your case and provide appropriate legal guidance.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Case Type
        </button>
        <button
          onClick={handleContinue}
          disabled={!isValidForm()}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            isValidForm()
              ? 'bg-red-500 text-white hover:bg-red-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Documentation →
        </button>
      </div>
    </div>
  );
};

// Asylum Step 3: Persecution Documentation
const AsylumStep3PersecutionDocs = ({ formData, setFormData, onNext, onBack }) => {
  const [persecutionDetails, setPersecutionDetails] = useState(formData.persecutionDetails || {
    typeOfPersecution: [],
    persecutors: '',
    governmentInvolvement: '',
    timeline: [],
    currentThreats: false,
    detailedAccount: '',
    physicalHarm: false,
    psychologicalHarm: false,
    witnessesExist: false
  });

  const persecutionTypes = [
    { id: 'political', label: 'Political opinion or activities', icon: '🗳️' },
    { id: 'religious', label: 'Religion or religious beliefs', icon: '🛐' },
    { id: 'nationality', label: 'Race, nationality, or ethnicity', icon: '🌍' },
    { id: 'social-group', label: 'Membership in particular social group', icon: '👥' },
    { id: 'gender', label: 'Gender-based persecution', icon: '⚧️' },
    { id: 'sexual-orientation', label: 'Sexual orientation or gender identity', icon: '🏳️‍🌈' },
    { id: 'domestic-violence', label: 'Domestic violence', icon: '🏠' },
    { id: 'gang-violence', label: 'Gang violence or organized crime', icon: '⚔️' },
    { id: 'government-oppression', label: 'Government oppression or corruption', icon: '🏛️' },
    { id: 'trafficking', label: 'Human trafficking', icon: '⛓️' }
  ];

  const handlePersecutionTypeChange = (typeId) => {
    const currentTypes = persecutionDetails.typeOfPersecution || [];
    let updatedTypes;
    
    if (currentTypes.includes(typeId)) {
      updatedTypes = currentTypes.filter(id => id !== typeId);
    } else {
      updatedTypes = [...currentTypes, typeId];
    }
    
    const updated = { ...persecutionDetails, typeOfPersecution: updatedTypes };
    setPersecutionDetails(updated);
    setFormData({ ...formData, persecutionDetails: updated });
  };

  const handleDetailChange = (field, value) => {
    const updated = { ...persecutionDetails, [field]: value };
    setPersecutionDetails(updated);
    setFormData({ ...formData, persecutionDetails: updated });
  };

  const isValidForm = () => {
    return persecutionDetails.typeOfPersecution?.length > 0 && 
           persecutionDetails.persecutors &&
           persecutionDetails.detailedAccount;
  };

  const handleContinue = () => {
    if (isValidForm()) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Persecution Documentation</h3>
        <p className="text-gray-400 text-lg">Provide details about the persecution you experienced</p>
      </div>

      {/* Trauma-Informed Notice */}
      <div className="bg-purple-900/20 border border-purple-400 p-4 rounded-lg">
        <h4 className="text-purple-300 font-semibold mb-2">💜 Trauma-Informed Approach</h4>
        <p className="text-purple-200 text-sm">
          We understand discussing persecution can be difficult. Take your time, and remember you can save your progress and return later. 
          If you need emotional support, consider reaching out to counseling services.
        </p>
      </div>

      {/* Types of Persecution */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">🎯 Type of Persecution Experienced</h4>
        <p className="text-gray-300 mb-4">Select all that apply to your situation:</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {persecutionTypes.map((type) => (
            <div
              key={type.id}
              onClick={() => handlePersecutionTypeChange(type.id)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                persecutionDetails.typeOfPersecution?.includes(type.id)
                  ? 'border-red-500 bg-red-900/20'
                  : 'border-gray-600 bg-gray-700 hover:border-red-400'
              }`}
            >
              <div className="flex items-center">
                <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                  persecutionDetails.typeOfPersecution?.includes(type.id) 
                    ? 'border-red-500 bg-red-500' 
                    : 'border-gray-500'
                }`}>
                  {persecutionDetails.typeOfPersecution?.includes(type.id) && (
                    <span className="text-white text-xs">✓</span>
                  )}
                </div>
                <span className="text-lg mr-2">{type.icon}</span>
                <span className={`text-sm ${
                  persecutionDetails.typeOfPersecution?.includes(type.id) ? 'text-white' : 'text-gray-300'
                }`}>
                  {type.label}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Perpetrator Information */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">👤 Perpetrator Information</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2">Who was responsible for the persecution? *</label>
            <select
              value={persecutionDetails.persecutors}
              onChange={(e) => handleDetailChange('persecutors', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
            >
              <option value="">Select perpetrator type</option>
              <option value="government">Government officials/agents</option>
              <option value="police-military">Police or military</option>
              <option value="family-member">Family member(s)</option>
              <option value="spouse-partner">Spouse or intimate partner</option>
              <option value="gangs">Gangs or organized crime</option>
              <option value="community-members">Community members</option>
              <option value="employers">Employers or traffickers</option>
              <option value="religious-groups">Religious groups</option>
              <option value="political-groups">Political groups</option>
              <option value="unknown-persons">Unknown persons</option>
              <option value="multiple">Multiple types</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Was the government involved or unable/unwilling to protect you?</label>
            <select
              value={persecutionDetails.governmentInvolvement}
              onChange={(e) => handleDetailChange('governmentInvolvement', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
            >
              <option value="">Select government involvement</option>
              <option value="government-perpetrator">Government was the perpetrator</option>
              <option value="government-unable">Government was unable to protect me</option>
              <option value="government-unwilling">Government was unwilling to protect me</option>
              <option value="government-complicit">Government was complicit or looked the other way</option>
              <option value="no-government-involvement">Government was not involved</option>
            </select>
          </div>
        </div>
      </div>

      {/* Detailed Account */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📝 Persecution Details</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2">
              Please provide a brief account of what happened *
            </label>
            <p className="text-gray-400 text-sm mb-2">
              You can provide general details now. More detailed statements can be developed later with legal assistance.
            </p>
            <textarea
              value={persecutionDetails.detailedAccount}
              onChange={(e) => handleDetailChange('detailedAccount', e.target.value)}
              placeholder="Briefly describe the persecution you experienced, when it occurred, and how it affected you..."
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-red-500 h-32"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Did you suffer physical harm?</label>
              <select
                value={persecutionDetails.physicalHarm ? 'yes' : 'no'}
                onChange={(e) => handleDetailChange('physicalHarm', e.target.value === 'yes')}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
              >
                <option value="">Select...</option>
                <option value="no">No physical harm</option>
                <option value="yes">Yes, physical harm occurred</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Did you suffer psychological harm?</label>
              <select
                value={persecutionDetails.psychologicalHarm ? 'yes' : 'no'}
                onChange={(e) => handleDetailChange('psychologicalHarm', e.target.value === 'yes')}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
              >
                <option value="">Select...</option>
                <option value="no">No psychological harm</option>
                <option value="yes">Yes, psychological harm occurred</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Are there witnesses to what happened?</label>
              <select
                value={persecutionDetails.witnessesExist ? 'yes' : 'no'}
                onChange={(e) => handleDetailChange('witnessesExist', e.target.value === 'yes')}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-red-500"
              >
                <option value="">Select...</option>
                <option value="no">No witnesses</option>
                <option value="yes">Yes, there are witnesses</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Support Resources */}
      <div className="bg-green-900/20 border border-green-400 p-4 rounded-lg">
        <h4 className="text-green-300 font-semibold mb-2">🤝 Support Resources</h4>
        <p className="text-green-200 text-sm mb-2">
          Remember that help is available. Consider these resources:
        </p>
        <ul className="text-green-200 text-sm space-y-1">
          <li>• National Suicide Prevention Lifeline: 988</li>
          <li>• Crisis Text Line: Text HOME to 741741</li>
          <li>• SAMHSA National Helpline: 1-800-662-4357</li>
          <li>• Local mental health and victim services organizations</li>
        </ul>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Safety Assessment
        </button>
        <button
          onClick={handleContinue}
          disabled={!isValidForm()}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            isValidForm()
              ? 'bg-red-500 text-white hover:bg-red-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Evidence Collection →
        </button>
      </div>
    </div>
  );
}
// Asylum Step 6: Legal Strategy Review
const AsylumStep6LegalStrategy = ({ formData, setFormData, onNext, onBack }) => {
  const [corrections, setCorrections] = useState(formData.corrections || []);
  const [strategicAnalysis, setStrategicAnalysis] = useState(null);

  React.useEffect(() => {
    generateStrategicAnalysis();
  }, []);

  const generateStrategicAnalysis = () => {
    const analysis = formData.analysisResults || {};
    const documents = formData.documents || [];
    const persecutionDetails = formData.persecutionDetails || {};
    const caseType = formData.caseType;

    // Generate case-specific strategic analysis
    const strategy = {
      legalBasis: generateLegalBasis(caseType, persecutionDetails),
      strengthAreas: analysis.strengths || [],
      evidenceGaps: analysis.issues || [],
      strategicRecommendations: generateStrategicRecommendations(caseType, persecutionDetails, documents),
      riskAssessment: generateRiskAssessment(formData),
      timelineConsiderations: generateTimelineConsiderations(caseType, formData.arrivalDate)
    };

    setStrategicAnalysis(strategy);
  };

  const generateLegalBasis = (caseType, persecutionDetails) => {
    const persecutionTypes = persecutionDetails.typeOfPersecution || [];
    
    switch(caseType) {
      case 'asylum':
        const grounds = persecutionTypes.map(type => {
          switch(type) {
            case 'political': return 'Political Opinion (INA § 101(a)(42))';
            case 'religious': return 'Religion (INA § 101(a)(42))';
            case 'nationality': return 'Race/Nationality (INA § 101(a)(42))';
            case 'social-group': return 'Particular Social Group (INA § 101(a)(42))';
            case 'gender': return 'Gender-based Particular Social Group';
            case 'sexual-orientation': return 'LGBTI+ Particular Social Group';
            default: return 'Protected Ground (INA § 101(a)(42))';
          }
        });
        return `Asylum eligibility under INA § 208 based on: ${grounds.join(', ')}. Must demonstrate persecution or well-founded fear of persecution.`;
        
      case 'vawa':
        return 'VAWA self-petition under INA § 204(a)(1)(A)(iii) for abused spouse. Must establish: (1) qualifying relationship, (2) abuse by USC/LPR spouse, (3) good moral character, (4) extreme hardship upon removal.';
        
      case 't-visa':
        return 'T nonimmigrant status under INA § 101(a)(15)(T) for trafficking victims. Must demonstrate: (1) victim of severe trafficking, (2) physical presence due to trafficking, (3) cooperation with law enforcement, (4) extreme hardship upon removal.';
        
      case 'u-visa':
        return 'U nonimmigrant status under INA § 101(a)(15)(U) for crime victims. Must establish: (1) victim of qualifying crime, (2) substantial physical/mental harm, (3) possession of information about crime, (4) law enforcement cooperation.';
        
      case 'withholding':
        return 'Withholding of removal under INA § 241(b)(3) and CAT protection. Higher burden than asylum - must show "clear probability" of persecution or torture.';
        
      default:
        return 'Protection-based relief under applicable immigration statutes.';
    }
  };

  const generateStrategicRecommendations = (caseType, persecutionDetails, documents) => {
    const recommendations = [];
    const medicalDocs = documents.filter(d => d.category === 'medical').length;
    const countryDocs = documents.filter(d => d.category === 'country-conditions').length;
    const witnessStmts = documents.filter(d => d.category === 'witness-statements').length;

    if (caseType === 'asylum') {
      if (medicalDocs === 0) {
        recommendations.push('Priority: Obtain comprehensive psychological evaluation documenting trauma and PTSD symptoms');
      }
      if (countryDocs === 0) {
        recommendations.push('Critical: Gather current country condition reports from State Department, UNHCR, and human rights organizations');
      }
      if (witnessStmts === 0) {
        recommendations.push('Recommended: Collect corroborating witness affidavits from family, friends, or community members');
      }
      if (!persecutionDetails.detailedAccount) {
        recommendations.push('Essential: Develop detailed, chronological personal statement with specific dates and locations');
      }
    }

    if (caseType === 'vawa') {
      recommendations.push('Document pattern of abuse with medical records, police reports, and photographs');
      recommendations.push('Establish qualifying relationship with marriage certificate and joint financial records');
      recommendations.push('Demonstrate good moral character with background check and character references');
      if (medicalDocs === 0) {
        recommendations.push('Critical: Medical documentation of injuries and psychological impact of abuse');
      }
    }

    if (caseType === 't-visa') {
      recommendations.push('Priority: Obtain law enforcement certification (Form I-914B) from investigating agency');
      recommendations.push('Document trafficking experience with detailed personal statement');
      recommendations.push('Show cooperation with law enforcement investigation or prosecution');
    }

    if (caseType === 'u-visa') {
      recommendations.push('Essential: Law enforcement certification of qualifying crime and cooperation');
      recommendations.push('Document substantial physical or mental harm from the crime');
      recommendations.push('Demonstrate ongoing cooperation with investigation/prosecution');
    }

    return recommendations.length > 0 ? recommendations : ['Case appears well-documented for current stage'];
  };

  const generateRiskAssessment = (data) => {
    const risks = [];
    const caseType = data.caseType;
    const arrivalDate = data.arrivalDate;
    const documents = data.documents || [];

    if (caseType === 'asylum' && arrivalDate) {
      const arrival = new Date(arrivalDate);
      const oneYearDeadline = new Date(arrival.getTime() + 365 * 24 * 60 * 60 * 1000);
      const now = new Date();
      
      if (now > oneYearDeadline) {
        risks.push('High Risk: One-year filing deadline passed - must establish exceptional circumstances or changed country conditions');
      } else if ((oneYearDeadline.getTime() - now.getTime()) < 90 * 24 * 60 * 60 * 1000) {
        risks.push('Medium Risk: Approaching one-year filing deadline - urgent filing needed');
      }
    }

    if (documents.length < 3) {
      risks.push('Medium Risk: Limited documentation may weaken credibility assessment');
    }

    if (!data.persecutionDetails?.detailedAccount) {
      risks.push('High Risk: Lack of detailed persecution account will significantly impact case');
    }

    return risks.length > 0 ? risks : ['No major procedural or substantive risks identified'];
  };

  const generateTimelineConsiderations = (caseType, arrivalDate) => {
    const considerations = [];

    if (caseType === 'asylum') {
      considerations.push('One-year filing deadline from arrival date (with limited exceptions)');
      considerations.push('150-day waiting period before work authorization eligibility');
      considerations.push('If denied, potential removal proceedings initiation');
    }

    if (caseType === 'vawa') {
      considerations.push('No specific filing deadline, but prompt filing recommended');
      considerations.push('Work authorization available immediately upon filing');
      considerations.push('Can lead to lawful permanent residence');
    }

    if (caseType === 't-visa' || caseType === 'u-visa') {
      considerations.push('Annual numerical limitations may cause delays');
      considerations.push('Work authorization available upon approval');
      considerations.push('Path to lawful permanent residence after specified period');
    }

    return considerations;
  };

  const addCorrection = () => {
    const newCorrection = {
      id: Date.now(),
      category: 'legal-strategy',
      description: '',
      importance: 'medium',
      completed: false
    };
    const updated = [...corrections, newCorrection];
    setCorrections(updated);
    setFormData({ ...formData, corrections: updated });
  };

  const updateCorrection = (id, field, value) => {
    const updated = corrections.map(corr => 
      corr.id === id ? { ...corr, [field]: value } : corr
    );
    setCorrections(updated);
    setFormData({ ...formData, corrections: updated });
  };

  const removeCorrection = (id) => {
    const updated = corrections.filter(corr => corr.id !== id);
    setCorrections(updated);
    setFormData({ ...formData, corrections: updated });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Legal Strategy Review</h3>
        <p className="text-gray-400 text-lg">Case-specific legal analysis and strategic recommendations</p>
      </div>

      {/* Case Summary */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📋 Case Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-700 p-4 rounded">
            <h5 className="text-white font-medium mb-2">Case Type</h5>
            <p className="text-red-400">{formData.caseType?.toUpperCase() || 'Not specified'}</p>
          </div>
          <div className="bg-gray-700 p-4 rounded">
            <h5 className="text-white font-medium mb-2">Documents</h5>
            <p className="text-blue-400">{(formData.documents || []).length} uploaded</p>
          </div>
          <div className="bg-gray-700 p-4 rounded">
            <h5 className="text-white font-medium mb-2">Case Strength</h5>
            <p className={`font-bold ${
              formData.analysisResults?.overall_score >= 70 ? 'text-emerald-400' :
              formData.analysisResults?.overall_score >= 50 ? 'text-yellow-400' :
              'text-red-400'
            }`}>
              {formData.analysisResults?.overall_score || 0}%
            </p>
          </div>
        </div>
      </div>

      {/* Legal Basis Analysis */}
      {strategicAnalysis && (
        <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
          <h4 className="text-blue-300 font-semibold mb-4">⚖️ Legal Basis Analysis</h4>
          <p className="text-blue-200 text-sm leading-relaxed">
            {strategicAnalysis.legalBasis}
          </p>
        </div>
      )}

      {/* Strategic Recommendations */}
      {strategicAnalysis?.strategicRecommendations && (
        <div className="bg-purple-900/20 border border-purple-400 p-6 rounded-lg">
          <h4 className="text-purple-300 font-semibold mb-4">💡 Strategic Recommendations</h4>
          <ul className="space-y-3">
            {strategicAnalysis.strategicRecommendations.map((rec, index) => (
              <li key={index} className="text-purple-200 text-sm flex items-start">
                <span className="text-purple-400 mr-2 mt-1 font-bold">{index + 1}.</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Risk Assessment */}
      {strategicAnalysis?.riskAssessment && strategicAnalysis.riskAssessment.length > 0 && (
        <div className="bg-red-900/20 border border-red-400 p-6 rounded-lg">
          <h4 className="text-red-300 font-semibold mb-4">⚠️ Risk Assessment</h4>
          <ul className="space-y-2">
            {strategicAnalysis.riskAssessment.map((risk, index) => (
              <li key={index} className="text-red-200 text-sm flex items-start">
                <span className="text-red-400 mr-2 mt-1">•</span>
                <span>{risk}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Timeline Considerations */}
      {strategicAnalysis?.timelineConsiderations && (
        <div className="bg-yellow-900/20 border border-yellow-400 p-6 rounded-lg">
          <h4 className="text-yellow-300 font-semibold mb-4">⏰ Timeline Considerations</h4>
          <ul className="space-y-2">
            {strategicAnalysis.timelineConsiderations.map((timeline, index) => (
              <li key={index} className="text-yellow-200 text-sm flex items-start">
                <span className="text-yellow-400 mr-2 mt-1">•</span>
                <span>{timeline}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Strategic Improvements */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📝 Custom Strategic Notes</h4>
        
        <div className="space-y-4">
          {corrections.map((correction) => (
            <div key={correction.id} className="bg-gray-700 p-4 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-300 mb-2">Category</label>
                  <select
                    value={correction.category}
                    onChange={(e) => updateCorrection(correction.id, 'category', e.target.value)}
                    className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white"
                  >
                    <option value="legal-strategy">Legal Strategy</option>
                    <option value="evidence">Evidence Enhancement</option>
                    <option value="documentation">Documentation</option>
                    <option value="timeline">Timeline Issues</option>
                    <option value="procedural">Procedural Concerns</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Priority</label>
                  <select
                    value={correction.importance}
                    onChange={(e) => updateCorrection(correction.id, 'importance', e.target.value)}
                    className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => removeCorrection(correction.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-gray-300 mb-2">Strategic Note</label>
                <textarea
                  value={correction.description}
                  onChange={(e) => updateCorrection(correction.id, 'description', e.target.value)}
                  placeholder="Add custom strategic notes, concerns, or action items..."
                  className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white h-24"
                />
              </div>
            </div>
          ))}
          
          <button
            onClick={addCorrection}
            className="w-full p-4 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-red-400 hover:text-red-400 transition-colors"
          >
            + Add Custom Strategic Note
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Country Analysis
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
        >
          Continue to Application Readiness →
        </button>
      </div>
    </div>
  );
};

// Asylum Step 7: Application Readiness
const AsylumStep7ApplicationReadiness = ({ formData, setFormData, onNext, onBack }) => {
  const [finalScore, setFinalScore] = useState(0);

  React.useEffect(() => {
    calculateFinalScore();
  }, []);

  const calculateFinalScore = () => {
    const analysis = formData.analysisResults || {};
    let score = analysis.overall_score || 0;

    // Bonus for corrections and improvements
    const corrections = formData.corrections || [];
    const completedCorrections = corrections.filter(c => c.completed).length;
    if (completedCorrections > 0) score += completedCorrections * 5;

    // Bonus for complete documentation
    const documents = formData.documents || [];
    if (documents.length >= 5) score += 10;
    if (documents.some(d => d.category === 'medical')) score += 5;
    if (documents.some(d => d.category === 'country-conditions')) score += 5;

    const finalCalculatedScore = Math.max(0, Math.min(100, Math.round(score)));
    setFinalScore(finalCalculatedScore);
    setFormData({ ...formData, finalScore: finalCalculatedScore });
  };

  const checkRequiredElements = () => {
    const documents = formData.documents || [];
    const persecutionDetails = formData.persecutionDetails || {};
    const personalDetails = formData.personalDetails || {};
    const safetyStatus = formData.safetyStatus || {};
    
    // Case-specific required documents
    let requiredDocs = [];
    if (formData.caseType === 'asylum') {
      requiredDocs = ['Medical Evidence', 'Country Conditions', 'Personal Statement', 'Identity Documents'];
    } else if (formData.caseType === 'vawa') {
      requiredDocs = ['Medical Evidence', 'Police Reports', 'Abuse Documentation', 'Marriage Certificate', 'Joint Financial Records'];
    } else if (formData.caseType === 't-visa') {
      requiredDocs = ['Law Enforcement Certification', 'Medical Evidence', 'Trafficking Evidence'];
    } else if (formData.caseType === 'u-visa') {
      requiredDocs = ['Law Enforcement Certification', 'Crime Evidence', 'Medical Evidence'];
    } else {
      requiredDocs = ['Supporting Evidence', 'Identity Documents', 'Country Conditions'];
    }

    // Check actual document categories uploaded
    const medicalDocs = documents.filter(d => d.category === 'medical').length;
    const countryDocs = documents.filter(d => d.category === 'country-conditions').length;
    const personalDocs = documents.filter(d => d.category === 'personal-documents').length;
    const witnessStmts = documents.filter(d => d.category === 'witness-statements').length;
    const photographs = documents.filter(d => d.category === 'photographs').length;
    const correspondence = documents.filter(d => d.category === 'correspondence').length;

    const checks = [
      {
        id: 'case_type',
        text: 'Protection case type selected',
        checked: !!formData.caseType,
        required: true,
        details: formData.caseType ? `Selected: ${formData.caseType.toUpperCase()}` : 'No case type selected'
      },
      {
        id: 'personal_details',
        text: 'Personal information completed',
        checked: !!(personalDetails.age && personalDetails.currentLocation),
        required: true,
        details: (personalDetails.age && personalDetails.currentLocation) ? 
          `Age: ${personalDetails.age}, Location: ${personalDetails.currentLocation}` :
          'Missing age or current location'
      },
      {
        id: 'persecution_details',
        text: 'Persecution details documented',
        checked: !!(persecutionDetails.typeOfPersecution?.length > 0 && 
                   persecutionDetails.persecutors && 
                   persecutionDetails.detailedAccount),
        required: true,
        details: (persecutionDetails.typeOfPersecution?.length > 0 && persecutionDetails.persecutors && persecutionDetails.detailedAccount) ?
          `Persecution types: ${persecutionDetails.typeOfPersecution?.length}, Persecutors: ${persecutionDetails.persecutors}` :
          'Missing persecution type, persecutors, or detailed account'
      },
      {
        id: 'country_origin',
        text: 'Country of origin and arrival details',
        checked: !!(formData.countryOfOrigin && formData.arrivalDate),
        required: true,
        details: (formData.countryOfOrigin && formData.arrivalDate) ?
          `From: ${formData.countryOfOrigin}, Arrived: ${formData.arrivalDate}` :
          'Missing country of origin or arrival date'
      },
      {
        id: 'safety_assessment',
        text: 'Safety assessment completed',
        checked: !!(safetyStatus.currentSafety && safetyStatus.persecutorLocation),
        required: true,
        details: (safetyStatus.currentSafety && safetyStatus.persecutorLocation) ?
          `Safety level: ${safetyStatus.currentSafety}, Persecutor location: ${safetyStatus.persecutorLocation}` :
          'Missing safety level or persecutor location information'
      },
      {
        id: 'supporting_documents',
        text: `Core supporting documents (${documents.length}/${requiredDocs.length} categories)`,
        checked: documents.length >= 3,
        required: true,
        details: documents.length >= 3 ?
          `${documents.length} documents uploaded across ${new Set(documents.map(d => d.category)).size} categories` :
          `Only ${documents.length} documents uploaded - need at least 3`
      },
      {
        id: 'medical_evidence',
        text: `Medical/psychological evidence (${medicalDocs} docs)`,
        checked: medicalDocs > 0,
        required: formData.caseType !== 'withholding',
        details: medicalDocs > 0 ?
          `${medicalDocs} medical document(s) uploaded` :
          'No medical evidence uploaded - strongly recommended for persecution claims'
      },
      {
        id: 'country_evidence',
        text: `Country condition evidence (${countryDocs} docs)`,
        checked: countryDocs > 0,
        required: formData.caseType === 'asylum',
        details: countryDocs > 0 ?
          `${countryDocs} country condition document(s) uploaded` :
          'No country condition evidence - required for asylum cases'
      },
      {
        id: 'witness_statements',
        text: `Witness statements (${witnessStmts} docs)`,
        checked: witnessStmts > 0,
        required: false,
        details: witnessStmts > 0 ?
          `${witnessStmts} witness statement(s) available` :
          'No witness statements - consider adding corroborating testimony'
      },
      {
        id: 'analysis_complete',
        text: 'Case analysis completed',
        checked: !!formData.analysisResults?.overall_score,
        required: true,
        details: formData.analysisResults?.overall_score ?
          `Analysis score: ${formData.analysisResults.overall_score}% (${formData.analysisResults.case_strength})` :
          'Case analysis not yet completed'
      }
    ];

    // Add VAWA-specific checks
    if (formData.caseType === 'vawa') {
      checks.push({
        id: 'abuse_evidence',
        text: 'Abuse documentation provided',
        checked: photographs > 0 || correspondence > 0 || medicalDocs > 0,
        required: true,
        details: (photographs > 0 || correspondence > 0 || medicalDocs > 0) ?
          `Abuse evidence: ${photographs} photos, ${correspondence} correspondence, ${medicalDocs} medical` :
          'No abuse documentation found - photos, medical records, or correspondence needed'
      });
    }

    return checks;
  };

  const readinessChecks = checkRequiredElements();
  const requiredItems = readinessChecks.filter(item => item.required);
  const completedRequired = requiredItems.filter(item => item.checked).length;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Application Readiness Assessment</h3>
        <p className="text-gray-400 text-lg">Review your protection case before generating the final packet</p>
      </div>

      {/* Final Score */}
      <div className="bg-gradient-to-r from-red-900/20 to-gray-900/20 p-8 rounded-lg border border-red-400/30">
        <div className="text-center">
          <div className="text-6xl font-bold text-red-400 mb-2">{finalScore}%</div>
          <div className="text-xl text-white mb-4">Protection Case Strength</div>
          <div className={`text-lg ${
            finalScore >= 80 ? 'text-emerald-400' :
            finalScore >= 60 ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {finalScore >= 80 ? 'Strong - Ready for submission' :
             finalScore >= 60 ? 'Good - Minor improvements recommended' :
             'Needs strengthening before submission'}
          </div>
        </div>
      </div>

      {/* Readiness Checklist */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">Application Readiness Checklist</h4>
        <div className="space-y-4">
          {readinessChecks.map((item) => (
            <div key={item.id} className="p-4 bg-gray-700 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-1">
                  <div className={`w-6 h-6 rounded border-2 mr-4 flex items-center justify-center mt-1 ${
                    item.checked ? 'bg-emerald-600 border-emerald-600' : 'border-red-500 bg-red-500/20'
                  }`}>
                    {item.checked ? (
                      <span className="text-white text-sm">✓</span>
                    ) : (
                      <span className="text-red-400 text-sm">✗</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className={`font-medium ${item.checked ? 'text-white' : 'text-gray-300'}`}>
                        {item.text}
                      </span>
                      {item.required && (
                        <span className="ml-2 text-red-400 text-xs font-bold">*REQUIRED</span>
                      )}
                    </div>
                    <div className={`text-sm mt-1 ${
                      item.checked ? 'text-emerald-300' : 'text-yellow-300'
                    }`}>
                      {item.details}
                    </div>
                  </div>
                </div>
                <span className={`text-sm font-medium px-3 py-1 rounded ${
                  item.checked 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-red-500 text-white'
                }`}>
                  {item.checked ? 'COMPLETE' : 'INCOMPLETE'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Missing Requirements */}
        {readinessChecks.filter(item => !item.checked && item.required).length > 0 && (
          <div className="mt-6 p-4 bg-red-900/20 border border-red-400 rounded-lg">
            <h5 className="text-red-300 font-medium mb-3">❌ Missing Required Items</h5>
            <ul className="space-y-2">
              {readinessChecks.filter(item => !item.checked && item.required).map((item) => (
                <li key={item.id} className="text-red-200 text-sm flex items-start">
                  <span className="text-red-400 mr-2 mt-1">•</span>
                  <div>
                    <strong>{item.text}:</strong> {item.details}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Case-Specific Requirements */}
        {formData.caseType && (
          <div className="mt-6 p-4 bg-blue-900/20 border border-blue-400 rounded-lg">
            <h5 className="text-blue-300 font-medium mb-3">📋 {formData.caseType.toUpperCase()} Case Requirements</h5>
            <div className="text-blue-200 text-sm">
              {formData.caseType === 'asylum' && (
                <ul className="space-y-1">
                  <li>• Must file within 1 year of arrival (with limited exceptions)</li>
                  <li>• Medical/psychological evidence strongly recommended</li>
                  <li>• Country condition reports required</li>
                  <li>• Detailed personal statement needed</li>
                </ul>
              )}
              {formData.caseType === 'vawa' && (
                <ul className="space-y-1">
                  <li>• Must demonstrate abuse by qualifying US citizen/LPR relative</li>
                  <li>• Medical evidence of abuse essential</li>
                  <li>• Police reports and incident documentation</li>
                  <li>• Evidence of relationship (marriage certificate, joint accounts)</li>
                  <li>• Good moral character documentation</li>
                </ul>
              )}
              {formData.caseType === 't-visa' && (
                <ul className="space-y-1">
                  <li>• Law enforcement certification (Form I-914B) required</li>
                  <li>• Evidence of severe trafficking</li>
                  <li>• Cooperation with law enforcement</li>
                  <li>• Physical presence due to trafficking</li>
                </ul>
              )}
              {formData.caseType === 'u-visa' && (
                <ul className="space-y-1">
                  <li>• Law enforcement certification required</li>
                  <li>• Victim of qualifying crime</li>
                  <li>• Substantial physical/mental harm</li>
                  <li>• Cooperation with law enforcement</li>
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Summary Statistics */}
        <div className="mt-6 p-4 bg-gray-900 rounded-lg">
          <h5 className="text-white font-medium mb-3">Readiness Summary</h5>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-2xl font-bold text-emerald-400">
                {completedRequired}/{requiredItems.length}
              </div>
              <div className="text-gray-400 text-sm">Required Items Complete</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">
                {readinessChecks.filter(item => item.checked).length}/{readinessChecks.length}
              </div>
              <div className="text-gray-400 text-sm">Total Items Complete</div>
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps Notice */}
      <div className="bg-purple-900/20 border border-purple-400 p-4 rounded-lg">
        <h4 className="text-purple-300 font-semibold mb-2">📋 Important Notes</h4>
        <ul className="text-purple-200 text-sm space-y-1">
          <li>• Consult with an immigration attorney for legal review</li>
          <li>• Ensure all documents are properly translated</li>
          <li>• Keep copies of all submitted materials</li>
          <li>• Be prepared for potential interview scheduling</li>
        </ul>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Legal Strategy
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
        >
          Generate Protection Packet →
        </button>
      </div>
    </div>
  );
};

// Asylum Step 8: Generate Protection Packet
const AsylumStep8GeneratePacket = ({ formData, setFormData, onBack, onClose }) => {
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [packet, setPacket] = useState(formData.packet || null);

  const generatePacket = async () => {
    setGenerating(true);
    
    try {
      // Real API call to generate asylum protection packet
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      // Format the request for asylum/protection case
      const petitionerDetails = {
        fullName: "Protection Seeker",
        status: formData.caseType || "asylum-seeker",
        employment: formData.persecutionDetails?.persecutors || "Unknown",
        income: "0",
        address: formData.personalDetails?.currentLocation || "Current US Location"
      };
      
      const beneficiaryDetails = {
        fullName: "Protection Seeker",
        status: formData.caseType || "asylum-seeker", 
        country: formData.countryOfOrigin || "Country of Origin",
        location: formData.personalDetails?.currentLocation || "Current US Location"
      };
      
      const relationshipDetails = {
        caseType: formData.caseType || "asylum",
        persecutionType: formData.persecutionDetails?.typeOfPersecution?.join(', ') || "",
        persecutors: formData.persecutionDetails?.persecutors || "",
        governmentInvolvement: formData.persecutionDetails?.governmentInvolvement || "",
        arrivalDate: formData.arrivalDate || "",
        countryOfOrigin: formData.countryOfOrigin || "",
        reasonForLeaving: formData.countryDetails?.reasonForLeaving || ""
      };
      
      const formattedDocuments = (formData.documents || []).map(doc => {
        // Create a copy without the file object for JSON serialization
        const { file, ...docWithoutFile } = doc;
        return {
          name: doc.name || "Document",
          category: doc.category || "supporting",
          date: doc.timestamp?.split('T')[0] || new Date().toISOString().split('T')[0],
          // Include OCR analysis results
          content_description: doc.content_description,
          case_relevance: doc.case_relevance,
          exhibit_description: doc.exhibit_description,
          immigration_value: doc.immigration_value,
          parties_mentioned: doc.parties_mentioned,
          key_dates: doc.key_dates,
          financial_amounts: doc.financial_amounts,
          red_flags: doc.red_flags,
          authenticity_indicators: doc.authenticity_indicators,
          extracted_text: doc.extracted_text,
          analysis: doc.analysis
        };
      });
      
      const response = await fetch(`${backendUrl}/api/ai/generate-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          case_type: `${(formData.caseType || 'Asylum').toUpperCase()} Protection Case`,
          petitioner_details: petitionerDetails,
          beneficiary_details: beneficiaryDetails,
          relationship_details: relationshipDetails,
          documents: formattedDocuments,
          analysis_results: formData.analysisResults || {},
          final_score: formData.finalScore || 50
        })
      });

      if (response.ok) {
        const result = await response.json();
        const enhancedResult = {
          ...result,
          contents: result.package_contents || result.contents || [
            'Protection Case Cover Letter.docx',
            'Personal Statement Template.docx', 
            'Country Conditions Report.docx',
            'Supporting Evidence Guide.docx',
            'Medical Evidence Guidelines.docx',
            'Witness Statement Templates.docx',
            'Legal Strategy Summary.docx',
            'Filing Instructions.docx'
          ],
          id: result.package_id || result.id || `AS-${Date.now()}`,
          downloadUrl: result.download_url || result.downloadUrl || `${backendUrl}/api/download-package/${result.package_id || result.id || `AS-${Date.now()}`}`,
          score: formData.finalScore || 0,
          title: `${formData.caseType?.toUpperCase() || 'Asylum'} Protection Case Package`,
          created: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        };
        setPacket(enhancedResult);
        setFormData({ ...formData, packet: enhancedResult });
      } else {
        // Fallback to simulated packet generation
        const simulatedPacket = generateAsylumPacket(formData);
        setPacket(simulatedPacket);
        setFormData({ ...formData, packet: simulatedPacket });
      }
    } catch (error) {
      console.error('Packet generation error:', error);
      // Fallback to simulated packet generation
      const simulatedPacket = generateAsylumPacket(formData);
      setPacket(simulatedPacket);
      setFormData({ ...formData, packet: simulatedPacket });
    }
    
    setGenerating(false);
  };

  const generateAsylumPacket = (data) => {
    const caseTypeNames = {
      'asylum': 'Asylum Application',
      'withholding': 'Withholding of Removal',
      'vawa': 'VAWA Self-Petition',
      't-visa': 'T-Visa Application',
      'u-visa': 'U-Visa Application'
    };

    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

    return {
      id: `AS-${Date.now()}`,
      type: 'asylum-protection-packet',
      title: `${caseTypeNames[data.caseType] || 'Protection'} Case Package`,
      created: new Date().toISOString(),
      score: data.finalScore || 0,
      contents: [
        'Protection Case Cover Letter.docx',
        'Personal Statement Template.docx',
        'Country Conditions Report.docx',
        'Supporting Evidence Guide.docx',
        'Medical Evidence Guidelines.docx',
        'Witness Statement Templates.docx',
        'Legal Strategy Summary.docx',
        'Filing Instructions.docx'
      ],
      downloadUrl: `${backendUrl}/api/download-package/AS-${Date.now()}`,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    };
  };

  const downloadPacket = async () => {
    if (packet && !downloading) {
      setDownloading(true);
      
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        
        // Always try to generate fresh package with user documents
        console.log('Generating fresh package with user documents...');
        
        const generateResponse = await fetch(`${backendUrl}/api/ai/generate-package`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            case_type: `${(formData.caseType || 'Asylum').toUpperCase()} Protection Case`,
            petitioner_details: {
              fullName: "Protection Seeker",
              status: formData.caseType || "asylum-seeker",
              employment: formData.persecutionDetails?.persecutors || "Unknown",
              income: "0",
              address: formData.personalDetails?.currentLocation || "Current US Location"
            },
            beneficiary_details: {
              fullName: "Protection Seeker",
              status: formData.caseType || "asylum-seeker",
              country: formData.countryOfOrigin || "Country of Origin", 
              location: formData.personalDetails?.currentLocation || "Current US Location"
            },
            relationship_details: {
              caseType: formData.caseType || "asylum",
              persecutionType: formData.persecutionDetails?.typeOfPersecution?.join(', ') || "",
              persecutors: formData.persecutionDetails?.persecutors || "",
              governmentInvolvement: formData.persecutionDetails?.governmentInvolvement || "",
              arrivalDate: formData.arrivalDate || "",
              countryOfOrigin: formData.countryOfOrigin || "",
              reasonForLeaving: formData.countryDetails?.reasonForLeaving || ""
            },
            documents: (formData.documents || []).map(doc => ({
              name: doc.name || "Document",
              category: doc.category || "supporting",
              date: doc.timestamp?.split('T')[0] || new Date().toISOString().split('T')[0]
            })),
            analysis_results: formData.analysisResults || {},
            final_score: formData.finalScore || 50
          })
        });

        if (!generateResponse.ok) {
          const errorText = await generateResponse.text();
          console.error('Package generation failed:', generateResponse.status, errorText);
          throw new Error(`Package generation failed: ${generateResponse.status} - ${errorText}`);
        }

        const generateResult = await generateResponse.json();
        console.log('Package generated successfully:', generateResult);
        
        // Now download the generated package
        const downloadUrl = generateResult.download_url || `${backendUrl}/api/download-package/${generateResult.package_id}`;
        console.log('Downloading from:', downloadUrl);
        
        const downloadResponse = await fetch(downloadUrl);
        if (!downloadResponse.ok) {
          const errorText = await downloadResponse.text();
          console.error('Download failed:', downloadResponse.status, errorText);
          throw new Error(`Download failed: ${downloadResponse.status} - ${errorText}`);
        }

        const blob = await downloadResponse.blob();
        console.log('Download blob received, size:', blob.size);
        
        if (blob.size === 0) {
          throw new Error('Downloaded file is empty');
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `asylum-protection-packet-${generateResult.package_id}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        console.log('Download completed successfully');
        
        // Update the packet data with the real package info
        const updatedPacket = {
          ...packet,
          id: generateResult.package_id,
          package_id: generateResult.package_id,
          downloadUrl: downloadUrl,
          contents: generateResult.package_contents || packet.contents
        };
        setPacket(updatedPacket);
        setFormData({ ...formData, packet: updatedPacket });
        
      } catch (error) {
        console.error('Download error:', error);
        console.log('Form data at error:', {
          caseType: formData.caseType,
          documentsCount: (formData.documents || []).length,
          analysisResults: !!formData.analysisResults
        });
        
        const errorMessage = error.message || 'Unable to generate package';
        alert(`Download failed: ${errorMessage}\n\nDetails:\n- Case Type: ${formData.caseType || 'Not specified'}\n- Documents: ${(formData.documents || []).length}\n- Backend URL: ${process.env.REACT_APP_BACKEND_URL || window.location.origin}\n\nThis might be due to:\n1. Network connectivity issues\n2. Server maintenance\n3. Invalid case data\n4. Backend service unavailable\n\nPlease try again in a few moments or contact support if the issue persists.`);
      } finally {
        setDownloading(false);
      }
    }
  };

  React.useEffect(() => {
    if (!packet) {
      generatePacket();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Protection Case Packet Generated</h3>
        <p className="text-gray-400 text-lg">Your comprehensive protection case package is ready</p>
      </div>

      {generating && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-red-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Generating Your Protection Case Packet</h4>
          <p className="text-gray-400">Creating professional documents and organizing your evidence...</p>
        </div>
      )}

      {!generating && packet && (
        <>
          {/* Success Message */}
          <div className="bg-emerald-900/20 border border-emerald-400 p-8 rounded-lg text-center">
            <div className="text-6xl mb-4">🛡️</div>
            <h3 className="text-3xl font-bold text-emerald-300 mb-4">Protection Packet Ready!</h3>
            <p className="text-emerald-200 text-lg mb-6">
              Your protection case package has been generated and is ready for download.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-emerald-300 font-medium">
              <div>Package ID: {packet.id}</div>
              <div>Case Strength: {packet.score}%</div>
              <div>Type: {formData.caseType?.toUpperCase()}</div>
            </div>
          </div>

          {/* Package Contents */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📦 Package Contents (All Word Documents)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {packet.contents.map((item, index) => (
                <div key={index} className="flex items-center bg-gray-700 p-3 rounded">
                  <span className="text-red-400 mr-3">📝</span>
                  <span className="text-white text-sm">{item}</span>
                  <span className="text-red-300 text-xs ml-auto">.docx</span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-red-900/20 border border-red-400 rounded">
              <p className="text-red-200 text-sm">
                <strong>📝 All documents are in Microsoft Word format</strong> - Ready for editing and legal review
              </p>
            </div>
          </div>

          {/* Download Section */}
          <div className="bg-gray-800 p-6 rounded-lg text-center">
            <h4 className="text-xl font-semibold text-white mb-4">📥 Download Your Protection Package</h4>
            <div className="mb-4">
              <p className="text-gray-300 text-sm mb-2">ZIP file containing all Word documents</p>
              <p className="text-gray-400 text-xs">Professional legal templates ready for attorney review</p>
            </div>
            <button
              onClick={downloadPacket}
              disabled={downloading}
              className={`px-12 py-6 rounded-xl font-bold text-2xl transition-colors mb-4 flex items-center justify-center mx-auto ${
                downloading 
                  ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                  : 'bg-red-500 text-white hover:bg-red-600'
              }`}
            >
              {downloading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-transparent mr-2"></div>
                  Preparing Download...
                </>
              ) : (
                <>
                  <span className="mr-2">📥</span>
                  Download Protection Packet (.zip)
                </>
              )}
            </button>
            <div className="space-y-2">
              <div className="text-gray-400 text-sm">
                Package ID: <span className="text-red-400 font-mono">{packet.id}</span>
              </div>
              <div className="text-gray-400 text-sm">
                Package expires: {new Date(packet.expiresAt).toLocaleDateString()}
              </div>
              <div className="text-emerald-400 text-sm">
                ✅ All documents are editable Word files (.docx)
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-purple-900/20 border border-purple-400 p-6 rounded-lg">
            <h4 className="text-purple-300 font-semibold mb-4">📋 Critical Next Steps</h4>
            <div className="space-y-3 text-purple-200">
              <div className="flex items-start">
                <span className="text-purple-400 mr-2 font-bold">1.</span>
                <span>Consult with an immigration attorney for legal review</span>
              </div>
              <div className="flex items-start">
                <span className="text-purple-400 mr-2 font-bold">2.</span>
                <span>Complete detailed personal statement with attorney assistance</span>
              </div>
              <div className="flex items-start">
                <span className="text-purple-400 mr-2 font-bold">3.</span>
                <span>Obtain professional translations for non-English documents</span>
              </div>
              <div className="flex items-start">
                <span className="text-purple-400 mr-2 font-bold">4.</span>
                <span>File your application within required deadlines</span>
              </div>
              <div className="flex items-start">
                <span className="text-purple-400 mr-2 font-bold">5.</span>
                <span>Prepare for potential USCIS interview</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Readiness Check
        </button>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg font-medium bg-emerald-400 text-black hover:bg-emerald-300 transition-colors"
        >
          Complete & Close
        </button>
      </div>
    </div>
  );
};

// Work Visa Step 1: Welcome & Visa Type Selection
const WorkVisaStep1Welcome = ({ formData, setFormData, onNext }) => {
  const [selectedVisaType, setSelectedVisaType] = useState(formData.visaType || '');

  const visaTypes = [
    { id: 'h1b', name: 'H-1B', description: 'Specialty Occupation Workers', common: true },
    { id: 'l1', name: 'L-1', description: 'Intracompany Transferees', common: true },
    { id: 'o1', name: 'O-1', description: 'Individuals with Extraordinary Ability', common: true },
    { id: 'tn', name: 'TN', description: 'NAFTA Professionals', common: false },
    { id: 'eb2', name: 'EB-2/3', description: 'Employment-Based Green Card', common: true },
    { id: 'other', name: 'Other', description: 'Other work visa type', common: false }
  ];

  const handleContinue = () => {
    if (selectedVisaType) {
      setFormData({ ...formData, visaType: selectedVisaType });
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="text-center mb-8 bg-gradient-to-r from-orange-900/20 to-gray-900/20 p-8 rounded-lg border border-orange-400/30">
        <div className="text-6xl mb-4">💼</div>
        <h3 className="text-4xl font-bold text-white mb-4">Welcome to Your Work Visa Journey</h3>
        <p className="text-gray-300 text-lg mb-4">AI-powered employment petition evidence assistant</p>
        <div className="flex justify-center items-center space-x-6 text-sm">
          <div className="flex items-center">
            <span className="text-orange-400 mr-2">✓</span>
            <span className="text-gray-300">Evidence Strategy</span>
          </div>
          <div className="flex items-center">
            <span className="text-orange-400 mr-2">✓</span>
            <span className="text-gray-300">Document Coaching</span>
          </div>
          <div className="flex items-center">
            <span className="text-orange-400 mr-2">✓</span>
            <span className="text-gray-300">USCIS-Ready Packets</span>
          </div>
        </div>
      </div>

      {/* Visa Type Selection */}
      <div>
        <h4 className="text-xl font-semibold text-white mb-4">Which type of work visa are you applying for?</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visaTypes.map((visa) => (
            <div
              key={visa.id}
              onClick={() => setSelectedVisaType(visa.id)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selectedVisaType === visa.id
                  ? 'border-orange-400 bg-orange-900/20'
                  : 'border-gray-700 bg-gray-800 hover:border-orange-400/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-white font-medium">{visa.name}</h5>
                  <p className="text-gray-400 text-sm">{visa.description}</p>
                </div>
                {visa.common && (
                  <span className="text-orange-400 text-xs bg-orange-900/20 px-2 py-1 rounded">COMMON</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-end">
        <button
          onClick={handleContinue}
          disabled={!selectedVisaType}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            selectedVisaType
              ? 'bg-orange-400 text-black hover:bg-orange-300'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Profile Setup →
        </button>
      </div>
    </div>
  );
};

// Work Visa Step 2: Profile & Job Overview
const WorkVisaStep2Profile = ({ formData, setFormData, onNext, onBack }) => {
  const [employerDetails, setEmployerDetails] = useState(formData.employerDetails || {});
  const [jobDetails, setJobDetails] = useState(formData.jobDetails || {});
  const [employeeDetails, setEmployeeDetails] = useState(formData.employeeDetails || {});

  const handleContinue = () => {
    setFormData({
      ...formData,
      employerDetails,
      jobDetails,
      employeeDetails
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Profile & Job Details</h3>
        <p className="text-gray-400 text-lg">Help us understand your employment situation</p>
      </div>

      {/* Employer Information */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">Employer Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Company Name *</label>
            <input
              type="text"
              placeholder="Company Name"
              value={employerDetails.companyName || ''}
              onChange={(e) => setEmployerDetails({...employerDetails, companyName: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-400"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Industry</label>
            <input
              type="text"
              placeholder="Technology, Healthcare, Finance, etc."
              value={employerDetails.industry || ''}
              onChange={(e) => setEmployerDetails({...employerDetails, industry: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-400"
            />
          </div>
        </div>
      </div>

      {/* Job Information */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">Job Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Job Title *</label>
            <input
              type="text"
              placeholder="Software Engineer, Marketing Manager, etc."
              value={jobDetails.title || ''}
              onChange={(e) => setJobDetails({...jobDetails, title: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-400"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Annual Salary</label>
            <input
              type="text"
              placeholder="$75,000"
              value={jobDetails.salary || ''}
              onChange={(e) => setJobDetails({...jobDetails, salary: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-400"
            />
          </div>
        </div>
      </div>

      {/* Employee Information */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">Your Background</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Education Level</label>
            <select
              value={employeeDetails.education || ''}
              onChange={(e) => setEmployeeDetails({...employeeDetails, education: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-orange-400"
            >
              <option value="">Select Education Level</option>
              <option value="high-school">High School</option>
              <option value="bachelors">Bachelor's Degree</option>
              <option value="masters">Master's Degree</option>
              <option value="phd">PhD</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Years of Experience</label>
            <input
              type="number"
              placeholder="5"
              value={employeeDetails.experience || ''}
              onChange={(e) => setEmployeeDetails({...employeeDetails, experience: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-400"
            />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Breakdown
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium bg-gray-400 text-black hover:bg-gray-300 transition-colors"
        >
          Continue to Strategy →
        </button>
      </div>
    </div>
  );
};

// Work Visa Step 3: Document Upload
const WorkVisaStep3Documents = ({ formData, setFormData, onNext, onBack }) => {
  const [uploadedDocs, setUploadedDocs] = useState(formData.documents || []);

  const documentCategories = [
    {
      id: 'required',
      title: 'Required Documents',
      icon: '📋',
      items: ['Offer Letter', 'Support Letter', 'Educational Credentials', 'Resume', 'Experience Letters']
    },
    {
      id: 'supporting',
      title: 'Supporting Documents',
      icon: '📄',
      items: ['Transcripts', 'Credential Eval', 'Visa Approvals', 'I-94', 'Passport']
    },
    {
      id: 'optional',
      title: 'Optional Documents',
      icon: '📎',
      items: ['Pay Stubs', 'Portfolio', 'Licenses', 'Awards']
    }
  ];

  const handleFileUpload = async (category, file) => {
    if (!file) return;
    
    try {
      // Convert file to base64 for AI analysis
      const fileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Call backend for actual document analysis with authentication
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/ai/analyze-uploaded-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',  // Include cookies for authentication
        body: JSON.stringify({
          document_file: fileData,
          document_name: file.name,
          document_type: category,
          case_context: {
            case_type: 'Work Visa Application',
            agent_type: 'work-visa',
            petitioner: formData.employerDetails?.fullName || 'Employer',
            beneficiary: formData.employeeDetails?.fullName || 'Employee'
          }
        })
      });

      let documentAnalysis;
      if (response.ok) {
        documentAnalysis = await response.json();
      } else {
        // Fallback if analysis fails
        documentAnalysis = {
          document_type: category,
          content_description: `Document requiring manual review: ${file.name}`,
          case_relevance: "Manual review required to determine relevance",
          exhibit_description: `Exhibit: ${file.name} requiring detailed manual review`,
          immigration_value: "Medium",
          analysis_error: "Automated analysis failed"
        };
      }

      // Create file object with OCR analysis results
      const newDoc = {
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        category: category,
        uploaded: true,
        uploadDate: new Date().toISOString(),
        // Add actual OCR analysis results
        analysis: documentAnalysis,
        content_description: documentAnalysis.content_description,
        parties_mentioned: documentAnalysis.parties_mentioned,
        key_dates: documentAnalysis.key_dates,
        case_relevance: documentAnalysis.case_relevance,
        exhibit_description: documentAnalysis.exhibit_description,
        immigration_value: documentAnalysis.immigration_value,
        red_flags: documentAnalysis.red_flags,
        authenticity_indicators: documentAnalysis.authenticity_indicators,
        extracted_text: documentAnalysis.extracted_text
      };
      
      const updatedDocs = [...uploadedDocs, newDoc];
      setUploadedDocs(updatedDocs);
    } catch (error) {
      console.error('Document analysis error:', error);
      
      // Add document with error indication
      const newDoc = {
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        category: category,
        uploaded: true,
        uploadDate: new Date().toISOString(),
        analysis_error: "Analysis failed - manual review required",
        content_description: `Document analysis failed for ${file.name}`,
        case_relevance: "Manual review required",
        exhibit_description: `Exhibit: ${file.name} (manual review required)`,
        immigration_value: "Unknown"
      };
      
      const updatedDocs = [...uploadedDocs, newDoc];
      setUploadedDocs(updatedDocs);
    }
  };

  const handleContinue = () => {
    setFormData({ ...formData, documents: uploadedDocs });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Document Upload</h3>
        <p className="text-gray-400 text-lg">Upload your work visa petition evidence</p>
      </div>

      {/* Document Categories */}
      <div className="space-y-6">
        {documentCategories.map((category) => (
          <div key={category.id} className="bg-gray-800 p-6 rounded-lg">
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-3">{category.icon}</span>
              <h4 className="text-xl font-semibold text-white">{category.title}</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {category.items.map((item, index) => (
                <div key={index} className="border border-gray-700 rounded p-3">
                  <div className="text-white text-sm mb-2">{item}</div>
                  <input
                    type="file"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleFileUpload(category.id, e.target.files[0]);
                      }
                    }}
                    className="w-full text-xs text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-xs file:bg-orange-400 file:text-black hover:file:bg-orange-300"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Upload Summary */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">Upload Summary</h4>
        <div className="text-lg font-bold text-orange-400 mb-2">{uploadedDocs.length} documents uploaded</div>
        {uploadedDocs.length > 0 && (
          <div className="space-y-2">
            {uploadedDocs.slice(-3).map((doc) => (
              <div key={doc.id} className="flex items-center text-gray-300">
                <span className="text-orange-400 mr-2">📄</span>
                <span className="text-sm">{doc.name}</span>
              </div>
            ))}
            {uploadedDocs.length > 3 && (
              <div className="text-gray-400 text-sm">...and {uploadedDocs.length - 3} more documents</div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Profile
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 rounded-lg font-medium bg-orange-400 text-black hover:bg-orange-300 transition-colors"
        >
          Continue to Analysis →
        </button>
      </div>
    </div>
  );
};

// Asylum & VAWA Agent Flow
export const AsylumProtectionAgentFlow = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    caseType: '',
    countryOfOrigin: '',
    arrivalDate: '',
    personalDetails: {
      age: '',
      gender: '',
      familyMembers: [],
      currentLocation: '',
      previousLocation: ''
    },
    persecutionDetails: {
      typeOfPersecution: [],
      persecutors: '',
      governmentInvolvement: '',
      timeline: [],
      currentThreats: false
    },
    travelHistory: {
      routeToUS: '',
      otherCountries: [],
      visaHistory: ''
    },
    evidenceDetails: {
      medicalRecords: false,
      psychologicalEvaluation: false,
      countryConditionEvidence: false,
      witnessStatements: false,
      photographs: false,
      documents: false
    },
    documents: [],
    analysisResults: {},
    recommendations: [],
    corrections: [],
    finalScore: 0,
    packet: null
  });

  const steps = [
    'Case Type & Intake',
    'Personal Safety Assessment', 
    'Persecution Documentation',
    'Evidence Collection',
    'Country Conditions Analysis',
    'Legal Strategy Review',
    'Application Readiness',
    'Generate Protection Packet'
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-3xl mr-4">🛡️</span>
              <div>
                <h2 className="text-2xl font-bold text-white">Asylum & Protection Agent</h2>
                <p className="text-gray-400">Humanitarian Relief Case Review</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Safety Notice */}
          <div className="mb-6 bg-red-900/20 border border-red-400 p-4 rounded-lg">
            <div className="flex items-center">
              <span className="text-red-400 mr-2">⚠️</span>
              <span className="text-red-300 font-medium">Safety Notice:</span>
            </div>
            <p className="text-red-200 mt-2 text-sm">
              If you are in immediate danger, contact emergency services or the National Domestic Violence Hotline: 1-800-799-7233
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Step {currentStep + 1} of {steps.length}</span>
              <span className="text-sm text-gray-400">{Math.round((currentStep / (steps.length - 1)) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-red-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
              ></div>
            </div>
            <p className="text-white font-medium mt-2">{steps[currentStep]}</p>
          </div>

          {/* Step Content */}
          {currentStep === 0 && (
            <AsylumStep1CaseType 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
            />
          )}
          {currentStep === 1 && (
            <AsylumStep2SafetyAssessment 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 2 && (
            <AsylumStep3PersecutionDocs 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 3 && (
            <AsylumStep4EvidenceCollection 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 4 && (
            <AsylumStep5CountryAnalysis 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 5 && (
            <AsylumStep6LegalStrategy 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 6 && (
            <AsylumStep7ApplicationReadiness 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 7 && (
            <AsylumStep8GeneratePacket 
              formData={formData} 
              setFormData={setFormData} 
              onBack={handleBack} 
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Work Visa Step 7: Final Review & Checklist
const WorkVisaStep7FinalReview = ({ formData, setFormData, onNext, onBack }) => {
  const [reviewChecklist, setReviewChecklist] = useState({});
  const [finalScore, setFinalScore] = useState(0);

  React.useEffect(() => {
    calculateFinalScore();
  }, []);

  const calculateFinalScore = () => {
    const analysis = formData.analysisResults || {};
    const corrections = formData.corrections || [];
    const recommendations = formData.recommendations || [];
    
    let score = analysis.overall_score || 0;
    
    // Bonus for corrections completed
    const completedCorrections = corrections.filter(c => c.status === 'completed').length;
    const totalCorrections = corrections.length;
    if (totalCorrections > 0) {
      score += (completedCorrections / totalCorrections) * 10;
    }
    
    // Penalty for high-priority recommendations not addressed
    const highPriorityRecs = recommendations.filter(r => r.priority === 'High').length;
    if (highPriorityRecs > 0) {
      score -= highPriorityRecs * 5;
    }
    
    const finalCalculatedScore = Math.max(0, Math.min(100, Math.round(score)));
    setFinalScore(finalCalculatedScore);
    setFormData({ ...formData, finalScore: finalCalculatedScore });
  };

  const checklistItems = [
    {
      id: 'visa_type',
      text: 'Visa type selected',
      checked: !!formData.visaType,
      required: true
    },
    {
      id: 'profile_complete',
      text: 'Profile information completed',
      checked: !!(formData.employerDetails?.companyName && formData.jobDetails?.title && formData.employeeDetails?.education),
      required: true
    },
    {
      id: 'documents_uploaded',
      text: 'Required documents uploaded',
      checked: (formData.documents || []).length >= 3,
      required: true
    },
    {
      id: 'analysis_complete',
      text: 'Case analysis completed',
      checked: !!formData.analysisResults?.overall_score,
      required: true
    },
    {
      id: 'corrections_addressed',
      text: 'Major corrections addressed',
      checked: (() => {
        const corrections = formData.corrections || [];
        const highPriorityCorrections = corrections.filter(c => c.priority === 'high');
        const completedHighPriority = highPriorityCorrections.filter(c => c.status === 'completed');
        // If no high priority corrections exist, mark as complete
        if (highPriorityCorrections.length === 0) return true;
        // Otherwise check if at least 80% of high priority corrections are complete
        return (completedHighPriority.length / highPriorityCorrections.length) >= 0.8;
      })(),
      required: false
    },
    {
      id: 'high_priority_recs',
      text: 'High-priority recommendations reviewed',
      checked: (() => {
        const recommendations = formData.recommendations || [];
        const highPriorityRecs = recommendations.filter(r => r.priority === 'High');
        // If there are high priority recommendations, this should be marked as needing attention
        return highPriorityRecs.length === 0;
      })(),
      required: false
    }
  ];

  const requiredItems = checklistItems.filter(item => item.required);
  const completedRequired = requiredItems.filter(item => item.checked).length;
  const allRequiredComplete = completedRequired === requiredItems.length;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Final Review & Checklist</h3>
        <p className="text-gray-400 text-lg">Comprehensive review before generating your work visa packet</p>
      </div>

      {/* Final Score */}
      <div className="bg-gradient-to-r from-orange-900/20 to-gray-900/20 p-8 rounded-lg border border-orange-400/30">
        <div className="text-center">
          <div className="text-6xl font-bold text-orange-400 mb-2">{finalScore}%</div>
          <div className="text-xl text-white mb-4">Final Case Strength</div>
          <div className={`text-lg ${
            finalScore >= 80 ? 'text-emerald-400' :
            finalScore >= 60 ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {finalScore >= 80 ? 'Excellent - Ready for submission' :
             finalScore >= 60 ? 'Good - Minor improvements possible' :
             'Needs improvement before submission'}
          </div>
        </div>
      </div>

      {/* Submission Checklist */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">Submission Readiness Checklist</h4>
        <div className="space-y-3">
          {checklistItems.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-3 bg-gray-700 rounded">
              <div className="flex items-center">
                <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                  item.checked ? 'bg-emerald-600 border-emerald-600' : 'border-gray-500'
                }`}>
                  {item.checked && <span className="text-white text-xs">✓</span>}
                </div>
                <span className={`${item.checked ? 'text-white' : 'text-gray-300'}`}>
                  {item.text}
                </span>
                {item.required && (
                  <span className="ml-2 text-red-400 text-xs">*required</span>
                )}
              </div>
              <span className={`text-sm ${item.checked ? 'text-emerald-400' : 'text-yellow-400'}`}>
                {item.checked ? 'Complete' : 'Pending'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Case Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="text-2xl font-bold text-orange-400 mb-2">
            {formData.visaType?.toUpperCase() || 'N/A'}
          </div>
          <div className="text-gray-400 text-sm">Visa Type</div>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="text-2xl font-bold text-orange-400 mb-2">
            {(formData.documents || []).length}
          </div>
          <div className="text-gray-400 text-sm">Documents</div>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="text-2xl font-bold text-orange-400 mb-2">
            {completedRequired}/{requiredItems.length}
          </div>
          <div className="text-gray-400 text-sm">Required Items</div>
        </div>
      </div>

      {/* Readiness Status */}
      <div className={`p-6 rounded-lg border-2 ${
        allRequiredComplete ? 'border-emerald-400 bg-emerald-900/20' : 'border-yellow-400 bg-yellow-900/20'
      }`}>
        <div className="flex items-center justify-center">
          <span className="text-2xl mr-3">{allRequiredComplete ? '✅' : '⚠️'}</span>
          <div>
            <h4 className={`text-lg font-semibold ${allRequiredComplete ? 'text-emerald-300' : 'text-yellow-300'}`}>
              {allRequiredComplete ? 'Ready for Packet Generation' : 'Some Items Incomplete'}
            </h4>
            <p className={`text-sm ${allRequiredComplete ? 'text-emerald-200' : 'text-yellow-200'}`}>
              {allRequiredComplete 
                ? 'All required items are complete. You can proceed to generate your work visa packet.'
                : 'Some items are incomplete, but you can still proceed to generate a packet. Complete items will be included.'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Proceed Regardless Message */}
      <div className="bg-gray-700/50 border border-gray-600 p-4 rounded-lg">
        <div className="flex items-center">
          <span className="text-blue-400 mr-2">ℹ️</span>
          <p className="text-gray-300 text-sm">
            <strong>You can proceed to packet generation regardless of your completion status.</strong> 
            The system will generate a packet with available information and provide guidance on missing items.
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Corrections
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium bg-orange-400 text-black hover:bg-orange-300 transition-colors"
        >
          Generate Work Visa Packet →
        </button>
      </div>
    </div>
  );
};

// Work Visa Step 8: Generate Output Packet
const WorkVisaStep8GeneratePacket = ({ formData, setFormData, onBack, onClose }) => {
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [packet, setPacket] = useState(formData.packet || null);

  const generatePacket = async () => {
    setGenerating(true);
    
    const visaTypeNames = {
      'h1b': 'H-1B Specialty Occupation',
      'l1': 'L-1 Intracompany Transferee',
      'o1': 'O-1 Extraordinary Ability',
      'tn': 'TN NAFTA Professional',
      'eb2': 'EB-2/3 Employment-Based Green Card',
      'other': 'Work Visa Application'
    };
    
    try {
      // Real API call to generate work visa packet
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      // Format the request to match backend expectations
      const petitionerDetails = {
        fullName: formData.employerDetails?.companyName || "Company Name",
        status: "us-employer",
        employment: formData.employerDetails?.industry || "Technology Company",
        income: "5000000", // Default company revenue
        address: formData.employerDetails?.address || "Business Address"
      };
      
      const beneficiaryDetails = {
        fullName: formData.employeeDetails?.fullName || "Employee Name",
        status: `${formData.visaType || 'h1b'}-applicant`,
        country: formData.employeeDetails?.country || "Unknown",
        location: formData.employeeDetails?.location || "Unknown"
      };
      
      const relationshipDetails = {
        visaType: formData.visaType || 'h1b',
        jobTitle: formData.jobDetails?.title || "Job Title",
        salary: formData.jobDetails?.salary || "100000",
        startDate: formData.jobDetails?.startDate || new Date().toISOString().split('T')[0],
        jobDescription: formData.jobDetails?.description || "Job description",
        education: formData.employeeDetails?.education || "bachelor",
        experience: formData.employeeDetails?.experience || "3 years"
      };
      
      // Format documents to match backend expectations
      const formattedDocuments = (formData.documents || []).map(doc => {
        // Create a copy without the file object for JSON serialization
        const { file, ...docWithoutFile } = doc;
        return {
          name: doc.name || "Document",
          category: doc.category || "other",
          date: doc.date || new Date().toISOString().split('T')[0],
          // Include OCR analysis results
          content_description: doc.content_description,
          case_relevance: doc.case_relevance,
          exhibit_description: doc.exhibit_description,
          immigration_value: doc.immigration_value,
          parties_mentioned: doc.parties_mentioned,
          key_dates: doc.key_dates,
          financial_amounts: doc.financial_amounts,
          red_flags: doc.red_flags,
          authenticity_indicators: doc.authenticity_indicators,
          extracted_text: doc.extracted_text,
          analysis: doc.analysis
        };
      });
      
      const response = await fetch(`${backendUrl}/api/ai/generate-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          case_type: `${(formData.visaType || 'h1b').toUpperCase()} Work Visa`,
          petitioner_details: petitionerDetails,
          beneficiary_details: beneficiaryDetails,
          relationship_details: relationshipDetails,
          documents: formattedDocuments,
          analysis_results: formData.analysisResults || {},
          final_score: formData.finalScore || 50
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Ensure the result has the proper structure with Word documents
        const enhancedResult = {
          ...result,
          contents: result.package_contents || result.contents || [
            'Work Visa Petition Cover Letter.docx',
            'Case Analysis Summary.docx',
            'Document Checklist & Evidence Guide.docx',
            'Employment Authorization Forms.docx',
            'Supporting Documentation Guide.docx',
            'Filing Instructions & Timeline.docx',
            'Legal Arguments & Justifications.docx',
            'Employer Support Letter Template.docx'
          ],
          id: result.package_id || result.id || `WV-${Date.now()}`,
          downloadUrl: result.download_url || result.downloadUrl || `${backendUrl}/api/download-package/${result.package_id || result.id || `WV-${Date.now()}`}`,
          score: formData.finalScore || 0,
          title: `${visaTypeNames[formData.visaType] || 'Work Visa'} Application Package`,
          created: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        };
        setPacket(enhancedResult);
        setFormData({ ...formData, packet: enhancedResult });
      } else {
        // Fallback to simulated packet generation
        const simulatedPacket = generateWorkVisaPacket(formData);
        setPacket(simulatedPacket);
        setFormData({ ...formData, packet: simulatedPacket });
      }
    } catch (error) {
      console.error('Packet generation error:', error);
      // Fallback to simulated packet generation
      const simulatedPacket = generateWorkVisaPacket(formData);
      setPacket(simulatedPacket);
      setFormData({ ...formData, packet: simulatedPacket });
    }
    
    setGenerating(false);
  };

  const generateWorkVisaPacket = (data) => {
    const visaTypeNames = {
      'h1b': 'H-1B Specialty Occupation',
      'l1': 'L-1 Intracompany Transferee',
      'o1': 'O-1 Extraordinary Ability',
      'tn': 'TN NAFTA Professional',
      'eb2': 'EB-2/3 Employment-Based Green Card',
      'other': 'Work Visa Application'
    };

    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

    return {
      id: `WV-${Date.now()}`,
      type: 'work-visa-packet',
      title: `${visaTypeNames[data.visaType] || 'Work Visa'} Application Package`,
      created: new Date().toISOString(),
      score: data.finalScore || 0,
      contents: [
        'Work Visa Petition Cover Letter.docx',
        'Case Analysis Summary.docx',
        'Document Checklist & Evidence Guide.docx',
        'Employment Authorization Forms.docx',
        'Supporting Documentation Guide.docx',
        'Filing Instructions & Timeline.docx',
        'Legal Arguments & Justifications.docx',
        'Employer Support Letter Template.docx'
      ],
      downloadUrl: `${backendUrl}/api/download-package/WV-${Date.now()}`,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    };
  };

  const downloadPacket = async () => {
    if (packet && !downloading) {
      setDownloading(true);
      
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        
        // Check if this is a real package (from API) or simulated package
        const isRealPacket = packet.downloadUrl && packet.downloadUrl.includes('/api/download-package/') && !packet.downloadUrl.includes('WV-');
        const isFromAPI = packet.package_id || (packet.id && !packet.id.startsWith('WV-'));
        
        if (isFromAPI || isRealPacket) {
          // Real package from API - use the provided download URL or construct it
          const downloadUrl = packet.downloadUrl || `${backendUrl}/api/download-package/${packet.package_id || packet.id}`;
          
          // Try to download the real package
          const response = await fetch(downloadUrl);
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `work-visa-packet-${packet.package_id || packet.id}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setDownloading(false);
            return;
          } else {
            console.warn('Real package download failed, falling back to generation');
          }
        }
        
        // Fallback: Generate package on-demand since simulated packet doesn't exist on backend
        console.log('Generating package on-demand for download...');
        
        // Format the request to match backend expectations
        const petitionerDetails = {
          fullName: formData.employerDetails?.companyName || "Company Name",
          status: "us-employer",
          employment: formData.employerDetails?.industry || "Technology Company",
          income: "5000000", // Default company revenue
          address: formData.employerDetails?.address || "Business Address"
        };
        
        const beneficiaryDetails = {
          fullName: formData.employeeDetails?.fullName || "Employee Name",
          status: `${formData.visaType || 'h1b'}-applicant`,
          country: formData.employeeDetails?.country || "Unknown",
          location: formData.employeeDetails?.location || "Unknown"
        };
        
        const relationshipDetails = {
          visaType: formData.visaType || 'h1b',
          jobTitle: formData.jobDetails?.title || "Job Title",
          salary: formData.jobDetails?.salary || "100000",
          startDate: formData.jobDetails?.startDate || new Date().toISOString().split('T')[0],
          jobDescription: formData.jobDetails?.description || "Job description",
          education: formData.employeeDetails?.education || "bachelor",
          experience: formData.employeeDetails?.experience || "3 years"
        };
        
        // Format documents to match backend expectations
        const formattedDocuments = (formData.documents || []).map(doc => {
          // Create a copy without the file object for JSON serialization
          const { file, ...docWithoutFile } = doc;
          return {
            name: doc.name || "Document",
            category: doc.category || "other",
            date: doc.date || new Date().toISOString().split('T')[0],
            // Include OCR analysis results
            content_description: doc.content_description,
            case_relevance: doc.case_relevance,
            exhibit_description: doc.exhibit_description,
            immigration_value: doc.immigration_value,
            parties_mentioned: doc.parties_mentioned,
            key_dates: doc.key_dates,
            financial_amounts: doc.financial_amounts,
            red_flags: doc.red_flags,
            authenticity_indicators: doc.authenticity_indicators,
            extracted_text: doc.extracted_text,
            analysis: doc.analysis
          };
        });
        
        const generateResponse = await fetch(`${backendUrl}/api/ai/generate-package`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            case_type: `${(formData.visaType || 'h1b').toUpperCase()} Work Visa`,
            petitioner_details: petitionerDetails,
            beneficiary_details: beneficiaryDetails,
            relationship_details: relationshipDetails,
            documents: formattedDocuments,
            analysis_results: formData.analysisResults || {},
            final_score: formData.finalScore || 50
          })
        });

        if (generateResponse.ok) {
          const generateResult = await generateResponse.json();
          const realDownloadUrl = generateResult.download_url || `${backendUrl}/api/download-package/${generateResult.package_id}`;
          
          // Download the newly generated package
          const downloadResponse = await fetch(realDownloadUrl);
          if (downloadResponse.ok) {
            const blob = await downloadResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `work-visa-packet-${generateResult.package_id}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            // Update the packet data with the real package info
            const updatedPacket = {
              ...packet,
              id: generateResult.package_id,
              package_id: generateResult.package_id,
              downloadUrl: realDownloadUrl
            };
            setPacket(updatedPacket);
            setFormData({ ...formData, packet: updatedPacket });
            setDownloading(false);
            return;
          } else {
            throw new Error(`Download failed: ${downloadResponse.status} ${downloadResponse.statusText}`);
          }
        } else {
          const errorText = await generateResponse.text();
          console.error('Package generation failed:', errorText);
          throw new Error(`Package generation failed: ${generateResponse.status} - ${errorText}`);
        }
        
        // Final fallback: Direct window.open
        throw new Error('Package generation failed');
        
      } catch (error) {
        console.error('Download error:', error);
        console.log('Form data at error:', formData);
        console.log('Packet data at error:', packet);
        
        // Show user-friendly error message with more details
        const errorMessage = error.message || 'Unable to generate package';
        alert(`Download failed: ${errorMessage}\n\nThis might be due to:\n1. Network connectivity issues\n2. Server maintenance\n3. Invalid case data\n\nPlease try again in a few moments or contact support if the issue persists.`);
        
        // Try one more fallback - direct navigation
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        try {
          window.open(`${backendUrl}/api/download-package/${packet.package_id || packet.id}`, '_blank');
        } catch (fallbackError) {
          console.error('Final fallback also failed:', fallbackError);
        }
      } finally {
        setDownloading(false);
      }
    }
  };

  React.useEffect(() => {
    if (!packet) {
      generatePacket();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Work Visa Packet Generated</h3>
        <p className="text-gray-400 text-lg">Your comprehensive work visa application package is ready</p>
      </div>

      {generating && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-400 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Generating Your Work Visa Packet</h4>
          <p className="text-gray-400">Creating professional documents and organizing your evidence...</p>
        </div>
      )}

      {!generating && packet && (
        <>
          {/* Success Message */}
          <div className="bg-emerald-900/20 border border-emerald-400 p-8 rounded-lg text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-3xl font-bold text-emerald-300 mb-4">Packet Ready!</h3>
            <p className="text-emerald-200 text-lg mb-6">
              Your work visa application package has been generated and is ready for download.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-emerald-300 font-medium">
              <div>Package ID: {packet.id}</div>
              <div>Final Score: {packet.score}%</div>
              <div>Type: {formData.visaType?.toUpperCase()}</div>
            </div>
          </div>

          {/* Package Contents */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📦 Package Contents (All Word Documents)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {packet.contents.map((item, index) => (
                <div key={index} className="flex items-center bg-gray-700 p-3 rounded">
                  <span className="text-blue-400 mr-3">📝</span>
                  <span className="text-white text-sm">{item}</span>
                  <span className="text-blue-300 text-xs ml-auto">.docx</span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-900/20 border border-blue-400 rounded">
              <p className="text-blue-200 text-sm">
                <strong>📝 All documents are in Microsoft Word format</strong> - Ready for editing and customization
              </p>
            </div>
          </div>

          {/* Download Section */}
          <div className="bg-gray-800 p-6 rounded-lg text-center">
            <h4 className="text-xl font-semibold text-white mb-4">📥 Download Your Complete Package</h4>
            <div className="mb-4">
              <p className="text-gray-300 text-sm mb-2">ZIP file containing all Word documents</p>
              <p className="text-gray-400 text-xs">Professional templates ready for customization</p>
            </div>
            <button
              onClick={downloadPacket}
              disabled={downloading}
              className={`px-8 py-4 rounded-lg font-bold text-lg transition-colors mb-4 flex items-center justify-center mx-auto ${
                downloading 
                  ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                  : 'bg-orange-400 text-black hover:bg-orange-300'
              }`}
            >
              {downloading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-transparent mr-2"></div>
                  Preparing Download...
                </>
              ) : (
                <>
                  <span className="mr-2">📥</span>
                  Download Work Visa Packet (.zip)
                </>
              )}
            </button>
            <div className="space-y-2">
              <div className="text-gray-400 text-sm">
                Package ID: <span className="text-orange-400 font-mono">{packet.id}</span>
              </div>
              <div className="text-gray-400 text-sm">
                Package expires: {new Date(packet.expiresAt).toLocaleDateString()}
              </div>
              <div className="text-emerald-400 text-sm">
                ✅ All documents are editable Word files (.docx)
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
            <h4 className="text-blue-300 font-semibold mb-4">📋 Next Steps</h4>
            <div className="space-y-3 text-blue-200">
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">1.</span>
                <span>Download and review all documents in your packet</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">2.</span>
                <span>Prepare original documents and certified copies as needed</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">3.</span>
                <span>Review filing fees and payment methods</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">4.</span>
                <span>Submit application according to USCIS guidelines</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">5.</span>
                <span>Keep copies of all submitted materials for your records</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Review
        </button>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg font-medium bg-emerald-400 text-black hover:bg-emerald-300 transition-colors"
        >
          Complete & Close
        </button>
      </div>
    </div>
  );
};

// Student/Exchange Visa Agent Flow
export const StudentVisaAgentFlow = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    visaType: '',
    schoolDetails: {
      schoolName: '',
      address: '',
      sevpId: '',
      programLevel: '',
      programName: '',
      startDate: '',
      endDate: ''
    },
    academicDetails: {
      gpa: '',
      previousEducation: '',
      englishProficiency: '',
      testScores: {}
    },
    financialDetails: {
      tuitionCost: '',
      livingExpenses: '',
      totalCost: '',
      fundingSource: '',
      sponsorInfo: {},
      bankBalance: ''
    },
    documents: [],
    analysisResults: {},
    recommendations: [],
    corrections: [],
    finalScore: 0,
    packet: null
  });

  console.log('StudentVisaAgentFlow is rendering', { isOpen, currentStep, formData });

  const steps = [
    'Visa Type & School Selection',
    'Academic Program Details', 
    'Financial Documentation',
    'Supporting Evidence Upload',
    'Academic Record Analysis',
    'Financial Capability Review',
    'Final Documentation Check',
    'Generate Student Visa Package'
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-3xl mr-4">🎓</span>
              <div>
                <h2 className="text-2xl font-bold text-white">Student & Exchange Visa Agent</h2>
                <p className="text-gray-400">F-1, J-1, M-1 Visa Application Process</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Step {currentStep + 1} of {steps.length}</span>
              <span className="text-sm text-gray-400">{Math.round((currentStep / (steps.length - 1)) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
              ></div>
            </div>
            <p className="text-white font-medium mt-2">{steps[currentStep]}</p>
          </div>

          {/* Step Content */}
          {currentStep === 0 && (
            <StudentVisaStep1VisaType 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
            />
          )}
          {currentStep === 1 && (
            <StudentVisaStep2AcademicProgram 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 2 && (
            <StudentVisaStep3FinancialDocs 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 3 && (
            <StudentVisaStep4DocumentUpload 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 4 && (
            <StudentVisaStep5AcademicAnalysis 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 5 && (
            <StudentVisaStep6FinancialReview 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 6 && (
            <StudentVisaStep7FinalCheck 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 7 && (
            <StudentVisaStep8GeneratePacket 
              formData={formData} 
              setFormData={setFormData} 
              onBack={handleBack} 
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Student Visa Step 1: Visa Type & School Selection
const StudentVisaStep1VisaType = ({ formData, setFormData, onNext }) => {
  const [selectedVisaType, setSelectedVisaType] = useState(formData.visaType || '');
  
  console.log('StudentVisaStep1VisaType component is rendering', { formData, selectedVisaType });

  const visaTypes = [
    {
      id: 'f1',
      title: 'F-1 Student Visa',
      description: 'Academic studies at colleges, universities, seminaries, conservatories',
      requirements: ['Full-time enrollment', 'Degree program', 'SEVIS approved school'],
      popular: true
    },
    {
      id: 'j1',
      title: 'J-1 Exchange Visitor',
      description: 'Exchange programs, research, teaching, training',
      requirements: ['Approved program sponsor', 'DS-2019 form', 'Home country requirement'],
      popular: false
    },
    {
      id: 'm1',
      title: 'M-1 Vocational Student',
      description: 'Vocational or technical training programs',
      requirements: ['Vocational program', 'Fixed time period', 'No degree program'],
      popular: false
    }
  ];

  const handleVisaSelection = (visaType) => {
    setSelectedVisaType(visaType);
    setFormData({ 
      ...formData, 
      visaType: visaType,
      schoolDetails: { ...formData.schoolDetails, programLevel: visaType === 'f1' ? 'undergraduate' : visaType === 'm1' ? 'vocational' : 'exchange' }
    });
  };

  const handleContinue = () => {
    if (selectedVisaType) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Select Your Visa Type</h3>
        <p className="text-gray-400 text-lg">Choose the student visa category that matches your study plans</p>
      </div>

      {/* Visa Type Cards */}
      <div className="grid grid-cols-1 gap-6">
        {visaTypes.map((visa) => (
          <div
            key={visa.id}
            onClick={() => handleVisaSelection(visa.id)}
            className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
              selectedVisaType === visa.id
                ? 'border-blue-500 bg-blue-900/20'
                : 'border-gray-700 bg-gray-800 hover:border-blue-400'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className={`w-6 h-6 rounded-full border-2 mr-4 flex items-center justify-center ${
                  selectedVisaType === visa.id ? 'border-blue-500 bg-blue-500' : 'border-gray-500'
                }`}>
                  {selectedVisaType === visa.id && <span className="text-white text-sm">✓</span>}
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-white flex items-center">
                    {visa.title}
                    {visa.popular && (
                      <span className="ml-2 bg-green-600 text-white text-xs px-2 py-1 rounded">
                        Most Common
                      </span>
                    )}
                  </h4>
                  <p className="text-gray-300 mt-1">{visa.description}</p>
                </div>
              </div>
            </div>

            <div className="ml-10">
              <h5 className="text-blue-300 font-medium mb-2">Key Requirements:</h5>
              <ul className="space-y-1">
                {visa.requirements.map((req, index) => (
                  <li key={index} className="text-gray-400 text-sm flex items-center">
                    <span className="text-blue-400 mr-2">•</span>
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* School Information Preview */}
      {selectedVisaType && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-4">📚 Next: School Information</h4>
          <p className="text-gray-300 text-sm">
            {selectedVisaType === 'f1' && 'You\'ll need your I-20 form and school details from a SEVP-approved institution.'}
            {selectedVisaType === 'j1' && 'You\'ll need your DS-2019 form from your program sponsor.'}
            {selectedVisaType === 'm1' && 'You\'ll need documentation from your vocational training program.'}
          </p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-end">
        <button
          onClick={handleContinue}
          disabled={!selectedVisaType}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            selectedVisaType
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Academic Program →
        </button>
      </div>
    </div>
  );
};

// Student Visa Step 2: Academic Program Details
const StudentVisaStep2AcademicProgram = ({ formData, setFormData, onNext, onBack }) => {
  const [schoolDetails, setSchoolDetails] = useState(formData.schoolDetails || {});
  const [academicDetails, setAcademicDetails] = useState(formData.academicDetails || {});

  const programLevels = {
    'f1': ['undergraduate', 'graduate', 'doctorate', 'language-program'],
    'j1': ['research', 'teaching', 'training', 'exchange'],
    'm1': ['vocational', 'technical', 'certificate']
  };

  const handleSchoolChange = (field, value) => {
    const updated = { ...schoolDetails, [field]: value };
    setSchoolDetails(updated);
    setFormData({ ...formData, schoolDetails: updated });
  };

  const handleAcademicChange = (field, value) => {
    const updated = { ...academicDetails, [field]: value };
    setAcademicDetails(updated);
    setFormData({ ...formData, academicDetails: updated });
  };

  const isValidForm = () => {
    return schoolDetails.schoolName && 
           schoolDetails.programName && 
           schoolDetails.startDate && 
           academicDetails.previousEducation;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Academic Program Details</h3>
        <p className="text-gray-400 text-lg">Provide information about your school and academic program</p>
      </div>

      {/* School Information */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">🏫 School Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">School Name *</label>
            <input
              type="text"
              placeholder="University/College Name"
              value={schoolDetails.schoolName || ''}
              onChange={(e) => handleSchoolChange('schoolName', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">SEVP ID (if known)</label>
            <input
              type="text"
              placeholder="School SEVP ID"
              value={schoolDetails.sevpId || ''}
              onChange={(e) => handleSchoolChange('sevpId', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-gray-300 mb-2">School Address</label>
            <input
              type="text"
              placeholder="Full school address"
              value={schoolDetails.address || ''}
              onChange={(e) => handleSchoolChange('address', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Program Information */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📚 Program Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Program Level</label>
            <select
              value={schoolDetails.programLevel || ''}
              onChange={(e) => handleSchoolChange('programLevel', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select Level</option>
              {(programLevels[formData.visaType] || []).map(level => (
                <option key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1).replace('-', ' ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Program Name *</label>
            <input
              type="text"
              placeholder="Degree/Program Name"
              value={schoolDetails.programName || ''}
              onChange={(e) => handleSchoolChange('programName', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Program Start Date *</label>
            <input
              type="date"
              value={schoolDetails.startDate || ''}
              onChange={(e) => handleSchoolChange('startDate', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Expected End Date</label>
            <input
              type="date"
              value={schoolDetails.endDate || ''}
              onChange={(e) => handleSchoolChange('endDate', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Academic Background */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">🎓 Academic Background</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Previous Education Level *</label>
            <select
              value={academicDetails.previousEducation || ''}
              onChange={(e) => handleAcademicChange('previousEducation', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select Education Level</option>
              <option value="high-school">High School</option>
              <option value="associate">Associate Degree</option>
              <option value="bachelor">Bachelor's Degree</option>
              <option value="master">Master's Degree</option>
              <option value="doctorate">Doctorate/PhD</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Current GPA (if applicable)</label>
            <input
              type="text"
              placeholder="e.g., 3.5/4.0"
              value={academicDetails.gpa || ''}
              onChange={(e) => handleAcademicChange('gpa', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">English Proficiency Test</label>
            <select
              value={academicDetails.englishProficiency || ''}
              onChange={(e) => handleAcademicChange('englishProficiency', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select Test</option>
              <option value="toefl">TOEFL</option>
              <option value="ielts">IELTS</option>
              <option value="duolingo">Duolingo</option>
              <option value="native">Native Speaker</option>
              <option value="none">Not Required</option>
            </select>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Visa Selection
        </button>
        <button
          onClick={onNext}
          disabled={!isValidForm()}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            isValidForm()
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Financial Info →
        </button>
      </div>
    </div>
  );
};

// Student Visa Step 3: Financial Documentation
const StudentVisaStep3FinancialDocs = ({ formData, setFormData, onNext, onBack }) => {
  const [financialDetails, setFinancialDetails] = useState(formData.financialDetails || {});

  const handleFinancialChange = (field, value) => {
    const updated = { ...financialDetails, [field]: value };
    setFinancialDetails(updated);
    
    // Auto-calculate total cost
    if (field === 'tuitionCost' || field === 'livingExpenses') {
      const tuition = parseFloat(updated.tuitionCost) || 0;
      const living = parseFloat(updated.livingExpenses) || 0;
      updated.totalCost = (tuition + living).toString();
    }
    
    setFormData({ ...formData, financialDetails: updated });
  };

  const isValidForm = () => {
    return financialDetails.tuitionCost && 
           financialDetails.livingExpenses && 
           financialDetails.fundingSource &&
           financialDetails.bankBalance;
  };

  const fundingSources = [
    'Personal/Family Funds',
    'Scholarship/Grant',
    'University Assistantship',
    'Government Sponsorship',
    'Employer Sponsorship',
    'Student Loan',
    'Combination of Sources'
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Financial Documentation</h3>
        <p className="text-gray-400 text-lg">Demonstrate your financial ability to support your studies</p>
      </div>

      {/* Cost Breakdown */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">💰 Education Costs</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Annual Tuition Cost (USD) *</label>
            <input
              type="number"
              placeholder="50000"
              value={financialDetails.tuitionCost || ''}
              onChange={(e) => handleFinancialChange('tuitionCost', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Annual Living Expenses (USD) *</label>
            <input
              type="number"
              placeholder="15000"
              value={financialDetails.livingExpenses || ''}
              onChange={(e) => handleFinancialChange('livingExpenses', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Total Annual Cost (USD)</label>
            <input
              type="number"
              value={financialDetails.totalCost || ''}
              disabled
              className="w-full bg-gray-600 border border-gray-600 rounded px-4 py-3 text-gray-300"
            />
          </div>
        </div>
      </div>

      {/* Funding Source */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">💳 Funding Source</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 mb-2">Primary Funding Source *</label>
            <select
              value={financialDetails.fundingSource || ''}
              onChange={(e) => handleFinancialChange('fundingSource', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">Select Funding Source</option>
              {fundingSources.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Available Bank Balance (USD) *</label>
            <input
              type="number"
              placeholder="100000"
              value={financialDetails.bankBalance || ''}
              onChange={(e) => handleFinancialChange('bankBalance', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Sponsor Information (if applicable) */}
      {financialDetails.fundingSource === 'Personal/Family Funds' && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">👨‍👩‍👧‍👦 Sponsor Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Sponsor Name</label>
              <input
                type="text"
                placeholder="Full name of sponsor"
                value={financialDetails.sponsorInfo?.name || ''}
                onChange={(e) => handleFinancialChange('sponsorInfo', { 
                  ...financialDetails.sponsorInfo, 
                  name: e.target.value 
                })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Relationship to Student</label>
              <select
                value={financialDetails.sponsorInfo?.relationship || ''}
                onChange={(e) => handleFinancialChange('sponsorInfo', { 
                  ...financialDetails.sponsorInfo, 
                  relationship: e.target.value 
                })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">Select Relationship</option>
                <option value="parent">Parent</option>
                <option value="spouse">Spouse</option>
                <option value="relative">Relative</option>
                <option value="self">Self</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Financial Summary */}
      <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
        <h4 className="text-blue-300 font-semibold mb-4">📊 Financial Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-400">
              ${parseInt(financialDetails.totalCost || 0).toLocaleString()}
            </div>
            <div className="text-blue-200 text-sm">Total Annual Cost</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-400">
              ${parseInt(financialDetails.bankBalance || 0).toLocaleString()}
            </div>
            <div className="text-blue-200 text-sm">Available Funds</div>
          </div>
          <div>
            <div className={`text-2xl font-bold ${
              parseInt(financialDetails.bankBalance || 0) >= parseInt(financialDetails.totalCost || 0)
                ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {parseInt(financialDetails.bankBalance || 0) >= parseInt(financialDetails.totalCost || 0) 
                ? '✓ Sufficient' : '⚠️ Insufficient'}
            </div>
            <div className="text-blue-200 text-sm">Fund Status</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Academic Info
        </button>
        <button
          onClick={onNext}
          disabled={!isValidForm()}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            isValidForm()
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Document Upload →
        </button>
      </div>
    </div>
  );
};

// Student Visa Step 4: Supporting Evidence Upload
const StudentVisaStep4DocumentUpload = ({ formData, setFormData, onNext, onBack }) => {
  const [documents, setDocuments] = useState(formData.documents || []);
  const [uploadCategory, setUploadCategory] = useState('required');

  const documentCategories = {
    required: {
      title: 'Required Documents',
      description: 'Essential documents for your student visa application',
      icon: '📋',
      priority: 'high',
      documents: [
        'I-20 Form (F-1) / DS-2019 (J-1)',
        'Passport (valid for 6+ months)',
        'Academic Transcripts',
        'Financial Support Documents',
        'SEVIS Fee Receipt',
        'Visa Application Form (DS-160)'
      ]
    },
    academic: {
      title: 'Academic Documents',
      description: 'Educational qualifications and test scores',
      icon: '🎓',
      priority: 'medium',
      documents: [
        'Diplomas/Degrees',
        'TOEFL/IELTS Scores',
        'GRE/GMAT Scores',
        'Letters of Recommendation',
        'Statement of Purpose',
        'Resume/CV'
      ]
    },
    financial: {
      title: 'Financial Documents',
      description: 'Proof of financial capability',
      icon: '💰',
      priority: 'high',
      documents: [
        'Bank Statements',
        'Sponsor Affidavit of Support',
        'Scholarship Letters',
        'Income Tax Returns',
        'Employment Letters',
        'Asset Valuations'
      ]
    },
    supporting: {
      title: 'Supporting Documents',
      description: 'Additional evidence to strengthen your case',
      icon: '📄',
      priority: 'low',
      documents: [
        'Research Proposal',
        'Publications',
        'Awards/Certificates',
        'Work Experience Letters',
        'Travel History',
        'Family Information'
      ]
    }
  };

  const handleFileUpload = async (files) => {
    const newDocs = [];
    
    for (const file of Array.from(files)) {
      try {
        // Convert file to base64 for AI analysis
        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Call backend for actual document analysis with authentication
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const response = await fetch(`${backendUrl}/api/ai/analyze-uploaded-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',  // Include cookies for authentication
          body: JSON.stringify({
            document_file: fileData,
            document_name: file.name,
            document_type: uploadCategory,
            case_context: {
              case_type: 'Student Visa Application',
              agent_type: 'student-visa',
              petitioner: formData.schoolDetails?.schoolName || 'School',
              beneficiary: formData.studentDetails?.fullName || 'Student'
            }
          })
        });

        let documentAnalysis;
        if (response.ok) {
          documentAnalysis = await response.json();
        } else {
          // Fallback if analysis fails
          documentAnalysis = {
            document_type: uploadCategory,
            content_description: `Document requiring manual review: ${file.name}`,
            case_relevance: "Manual review required to determine relevance",
            exhibit_description: `Exhibit: ${file.name} requiring detailed manual review`,
            immigration_value: "Medium",
            analysis_error: "Automated analysis failed"
          };
        }

        // Create file object with OCR analysis results
        const newDoc = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          category: uploadCategory,
          uploaded: true,
          timestamp: new Date().toISOString(),
          // Add actual OCR analysis results
          analysis: documentAnalysis,
          content_description: documentAnalysis.content_description,
          parties_mentioned: documentAnalysis.parties_mentioned,
          key_dates: documentAnalysis.key_dates,
          case_relevance: documentAnalysis.case_relevance,
          exhibit_description: documentAnalysis.exhibit_description,
          immigration_value: documentAnalysis.immigration_value,
          red_flags: documentAnalysis.red_flags,
          authenticity_indicators: documentAnalysis.authenticity_indicators,
          extracted_text: documentAnalysis.extracted_text
        };
        
        newDocs.push(newDoc);
      } catch (error) {
        console.error('Document analysis error:', error);
        
        // Add document with error indication
        const newDoc = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          category: uploadCategory,
          uploaded: true,
          timestamp: new Date().toISOString(),
          analysis_error: "Analysis failed - manual review required",
          content_description: `Document analysis failed for ${file.name}`,
          case_relevance: "Manual review required",
          exhibit_description: `Exhibit: ${file.name} (manual review required)`,
          immigration_value: "Unknown"
        };
        
        newDocs.push(newDoc);
      }
    }
    
    const updatedDocs = [...documents, ...newDocs];
    setDocuments(updatedDocs);
    setFormData({ ...formData, documents: updatedDocs });
  };

  const removeDocument = (docId) => {
    const updatedDocs = documents.filter(doc => doc.id !== docId);
    setDocuments(updatedDocs);
    setFormData({ ...formData, documents: updatedDocs });
  };

  const getDocumentsByCategory = (category) => {
    return documents.filter(doc => doc.category === category);
  };

  const getTotalDocuments = () => documents.length;
  const getRequiredDocuments = () => getDocumentsByCategory('required').length;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Document Upload</h3>
        <p className="text-gray-400 text-lg">Upload all required and supporting documents for your visa application</p>
      </div>

      {/* Document Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(documentCategories).map(([key, category]) => (
          <div key={key} className="bg-gray-800 p-4 rounded-lg text-center">
            <div className="text-2xl mb-2">{category.icon}</div>
            <div className="text-lg font-bold text-white">{getDocumentsByCategory(key).length}</div>
            <div className="text-gray-400 text-sm">{category.title}</div>
          </div>
        ))}
      </div>

      {/* Category Selection */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(documentCategories).map(([key, category]) => (
          <button
            key={key}
            onClick={() => setUploadCategory(key)}
            className={`p-4 rounded-lg border-2 transition-all ${
              uploadCategory === key
                ? 'border-blue-500 bg-blue-900/20'
                : 'border-gray-700 bg-gray-800 hover:border-blue-400'
            }`}
          >
            <div className="text-2xl mb-2">{category.icon}</div>
            <h4 className="text-white font-medium mb-1">{category.title}</h4>
            <p className="text-gray-400 text-xs">{category.description}</p>
            <div className={`text-xs mt-2 px-2 py-1 rounded ${
              category.priority === 'high' ? 'bg-red-900/20 text-red-300' :
              category.priority === 'medium' ? 'bg-yellow-900/20 text-yellow-300' :
              'bg-blue-900/20 text-blue-300'
            }`}>
              {category.priority} priority
            </div>
          </button>
        ))}
      </div>

      {/* Expected Documents */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">
          Expected {documentCategories[uploadCategory].title}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {documentCategories[uploadCategory].documents.map((doc, index) => (
            <div key={index} className="flex items-center text-gray-300 text-sm">
              <span className="text-blue-400 mr-2">•</span>
              {doc}
            </div>
          ))}
        </div>
      </div>

      {/* File Upload Area */}
      <div className="bg-gray-800 p-6 rounded-lg border-2 border-dashed border-gray-600">
        <div className="text-center">
          <div className="text-4xl mb-4">📁</div>
          <h4 className="text-xl font-semibold text-white mb-2">
            Upload {documentCategories[uploadCategory].title}
          </h4>
          <p className="text-gray-400 mb-4">Drag & drop files or click to browse</p>
          <input
            type="file"
            multiple
            onChange={(e) => handleFileUpload(e.target.files)}
            className="hidden"
            id="student-doc-upload"
          />
          <label
            htmlFor="student-doc-upload"
            className="bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors cursor-pointer inline-block"
          >
            Choose Files
          </label>
        </div>
      </div>

      {/* Uploaded Documents */}
      {documents.length > 0 && (
        <div className="bg-gray-800 p-6 rounded-lg">
          <h4 className="text-xl font-semibold text-white mb-4">
            Uploaded Documents ({documents.length} total)
          </h4>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between bg-gray-700 p-3 rounded">
                <div className="flex items-center flex-1">
                  <span className="text-blue-400 mr-3">📄</span>
                  <div className="flex-1">
                    <div className="text-white text-sm">{doc.name}</div>
                    <div className="text-gray-400 text-xs">
                      {documentCategories[doc.category]?.title} • {(doc.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                    {/* Show OCR analysis results */}
                    {doc.content_description && (
                      <div className="mt-2 space-y-1 text-sm">
                        <div className="text-gray-300">
                          <span className="text-blue-300 font-medium">Content:</span> {doc.content_description}
                        </div>
                        {doc.case_relevance && (
                          <div className="text-gray-300">
                            <span className="text-green-300 font-medium">Relevance:</span> {doc.case_relevance}
                          </div>
                        )}
                        {doc.immigration_value && (
                          <div className="text-gray-300">
                            <span className="text-purple-300 font-medium">Value:</span> {doc.immigration_value}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Show error if analysis failed */}
                    {doc.analysis_error && (
                      <div className="mt-2 text-sm text-yellow-400">
                        <span className="font-medium">Analysis:</span> {doc.analysis_error}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => removeDocument(doc.id)}
                  className="text-red-400 hover:text-red-300 text-sm px-2 py-1"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Progress */}
      <div className="bg-blue-900/20 border border-blue-400 p-4 rounded-lg">
        <h5 className="text-blue-300 font-medium mb-2">📈 Upload Progress</h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-xl font-bold text-blue-400">{getTotalDocuments()}</div>
            <div className="text-blue-200 text-sm">Total Documents</div>
          </div>
          <div>
            <div className="text-xl font-bold text-blue-400">{getRequiredDocuments()}</div>
            <div className="text-blue-200 text-sm">Required Documents</div>
          </div>
          <div>
            <div className={`text-xl font-bold ${getRequiredDocuments() >= 3 ? 'text-emerald-400' : 'text-yellow-400'}`}>
              {getRequiredDocuments() >= 3 ? 'Good' : 'Need More'}
            </div>
            <div className="text-blue-200 text-sm">Status</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Financial Info
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors"
        >
          Continue to Analysis →
        </button>
      </div>
    </div>
  );
};

// Student Visa Step 5: Academic Record Analysis
const StudentVisaStep5AcademicAnalysis = ({ formData, setFormData, onNext, onBack }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(formData.analysisResults || {});

  const performAnalysis = async () => {
    setAnalyzing(true);
    
    try {
      // Real API call to analyze student visa case
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/ai/analyze-case`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          caseType: 'student-visa',
          visaType: formData.visaType,
          schoolDetails: formData.schoolDetails,
          academicDetails: formData.academicDetails,
          financialDetails: formData.financialDetails,
          documents: formData.documents
        })
      });

      if (response.ok) {
        const result = await response.json();
        setAnalysisResults(result);
        setFormData({ ...formData, analysisResults: result });
      } else {
        // Fallback to dynamic analysis if API fails
        const dynamicAnalysis = generateStudentVisaAnalysis(formData);
        setAnalysisResults(dynamicAnalysis);
        setFormData({ ...formData, analysisResults: dynamicAnalysis });
      }
    } catch (error) {
      console.error('Analysis error:', error);
      // Fallback to dynamic analysis
      const dynamicAnalysis = generateStudentVisaAnalysis(formData);
      setAnalysisResults(dynamicAnalysis);
      setFormData({ ...formData, analysisResults: dynamicAnalysis });
    }
    
    setAnalyzing(false);
  };

  const generateStudentVisaAnalysis = (data) => {
    const documents = data.documents || [];
    const requiredDocs = ['I-20/DS-2019', 'Passport', 'Financial Support', 'Academic Transcripts', 'SEVIS Receipt'];
    
    // Calculate academic strength
    let academicScore = 50;
    if (data.academicDetails?.previousEducation) {
      const eduLevels = { 'high-school': 10, 'associate': 15, 'bachelor': 20, 'master': 25, 'doctorate': 30 };
      academicScore += eduLevels[data.academicDetails.previousEducation] || 10;
    }
    if (data.academicDetails?.gpa) {
      const gpa = parseFloat(data.academicDetails.gpa);
      if (gpa >= 3.5) academicScore += 20;
      else if (gpa >= 3.0) academicScore += 15;
      else if (gpa >= 2.5) academicScore += 10;
    }
    if (data.academicDetails?.englishProficiency && data.academicDetails.englishProficiency !== 'none') {
      academicScore += 15;
    }

    // Calculate financial strength
    let financialScore = 50;
    const totalCost = parseFloat(data.financialDetails?.totalCost || 0);
    const bankBalance = parseFloat(data.financialDetails?.bankBalance || 0);
    if (bankBalance >= totalCost * 2) financialScore += 30;
    else if (bankBalance >= totalCost * 1.5) financialScore += 20;
    else if (bankBalance >= totalCost) financialScore += 10;
    else if (bankBalance >= totalCost * 0.5) financialScore += 5;

    // Calculate document strength
    const documentScore = Math.min(100, (documents.length / 8) * 100);

    const overallScore = Math.min(100, Math.round((academicScore + financialScore + documentScore) / 3));

    // Generate strengths and issues
    const strengths = [];
    const issues = [];

    if (academicScore >= 70) strengths.push('Strong academic background');
    if (financialScore >= 70) strengths.push('Adequate financial support demonstrated');
    if (documents.length >= 6) strengths.push('Comprehensive document submission');
    if (data.schoolDetails?.sevpId) strengths.push('SEVP-approved institution');

    if (academicScore < 60) issues.push('Academic credentials may need strengthening');
    if (financialScore < 60) issues.push('Insufficient financial documentation');
    if (documents.length < 5) issues.push('Missing critical supporting documents');
    if (!data.schoolDetails?.startDate) issues.push('Program start date not specified');

    return {
      overall_score: overallScore,
      academic_strength: academicScore,
      financial_strength: financialScore,
      document_completeness: documentScore,
      strengths: strengths.length > 0 ? strengths : ['Basic information provided'],
      issues: issues.length > 0 ? issues : ['No major issues identified'],
      missing_documents: requiredDocs.filter(req => 
        !documents.some(doc => doc.name.toLowerCase().includes(req.toLowerCase().split('/')[0]))
      ),
      recommendations: generateStudentVisaRecommendations(data)
    };
  };

  const generateStudentVisaRecommendations = (data) => {
    const recommendations = [];
    const documents = data.documents || [];
    
    if (documents.length < 6) {
      recommendations.push('Upload additional supporting documents to strengthen your application');
    }
    if (!data.academicDetails?.englishProficiency || data.academicDetails.englishProficiency === 'none') {
      recommendations.push('Complete English proficiency testing (TOEFL/IELTS) if required');
    }
    if (!data.financialDetails?.fundingSource) {
      recommendations.push('Clearly document your funding source with supporting evidence');
    }
    if (!data.schoolDetails?.sevpId) {
      recommendations.push('Verify your school is SEVP-approved and obtain SEVP ID');
    }
    
    return recommendations.length > 0 ? recommendations : ['Application appears well-prepared for submission'];
  };

  React.useEffect(() => {
    if (Object.keys(analysisResults).length === 0) {
      performAnalysis();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Academic Record Analysis</h3>
        <p className="text-gray-400 text-lg">AI-powered analysis of your academic qualifications and visa readiness</p>
      </div>

      {analyzing && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Analyzing Your Academic Profile</h4>
          <p className="text-gray-400">Our AI is evaluating your academic credentials, program fit, and visa readiness...</p>
        </div>
      )}

      {!analyzing && analysisResults.overall_score !== undefined && (
        <>
          {/* Overall Score */}
          <div className="bg-gradient-to-r from-blue-900/20 to-gray-900/20 p-8 rounded-lg border border-blue-400/30">
            <div className="text-center">
              <div className="text-6xl font-bold text-blue-400 mb-2">{analysisResults.overall_score}%</div>
              <div className="text-xl text-white mb-4">Student Visa Readiness Score</div>
              <div className="text-gray-300">
                {analysisResults.overall_score >= 80 ? 'Excellent - Strong visa application' :
                 analysisResults.overall_score >= 60 ? 'Good - Some areas for improvement' :
                 'Needs strengthening before submission'}
              </div>
            </div>
          </div>

          {/* Analysis Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">✅ Application Strengths</h4>
              <ul className="space-y-2">
                {analysisResults.strengths?.map((strength, index) => (
                  <li key={index} className="flex items-start text-emerald-300">
                    <span className="text-emerald-400 mr-2">•</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">⚠️ Areas for Improvement</h4>
              <ul className="space-y-2">
                {analysisResults.issues?.map((issue, index) => (
                  <li key={index} className="flex items-start text-yellow-300">
                    <span className="text-yellow-400 mr-2">•</span>
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Missing Documents */}
          {analysisResults.missing_documents && analysisResults.missing_documents.length > 0 && (
            <div className="bg-red-900/20 border border-red-400 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-red-300 mb-4">📋 Missing Critical Documents</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {analysisResults.missing_documents.map((doc, index) => (
                  <div key={index} className="bg-red-900/30 p-3 rounded">
                    <span className="text-red-200">{doc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detailed Metrics */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-lg font-semibold text-white mb-4">📊 Detailed Analysis</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">Academic Strength</span>
                  <span className="text-blue-400 font-bold">{Math.round(analysisResults.academic_strength)}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-blue-400 h-2 rounded-full"
                    style={{ width: `${analysisResults.academic_strength}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">Financial Strength</span>
                  <span className="text-blue-400 font-bold">{Math.round(analysisResults.financial_strength)}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-blue-400 h-2 rounded-full"
                    style={{ width: `${analysisResults.financial_strength}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">Document Completeness</span>
                  <span className="text-blue-400 font-bold">{Math.round(analysisResults.document_completeness)}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-blue-400 h-2 rounded-full"
                    style={{ width: `${analysisResults.document_completeness}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Documents
        </button>
        <button
          onClick={() => {
            if (!analyzing && analysisResults.overall_score !== undefined) {
              onNext();
            }
          }}
          disabled={analyzing || analysisResults.overall_score === undefined}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            !analyzing && analysisResults.overall_score !== undefined
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Financial Review →
        </button>
      </div>
    </div>
  );
};

// Student Visa Step 6: Financial Capability Review
const StudentVisaStep6FinancialReview = ({ formData, setFormData, onNext, onBack }) => {
  const [financialAnalysis, setFinancialAnalysis] = useState({});

  React.useEffect(() => {
    generateFinancialAnalysis();
  }, []);

  const generateFinancialAnalysis = () => {
    const financial = formData.financialDetails || {};
    const totalCost = parseFloat(financial.totalCost || 0);
    const bankBalance = parseFloat(financial.bankBalance || 0);
    
    const adequacyRatio = bankBalance / totalCost;
    let adequacyLevel = 'Insufficient';
    let riskLevel = 'High';
    
    if (adequacyRatio >= 2) {
      adequacyLevel = 'Excellent';
      riskLevel = 'Low';
    } else if (adequacyRatio >= 1.5) {
      adequacyLevel = 'Very Good';
      riskLevel = 'Low';
    } else if (adequacyRatio >= 1.2) {
      adequacyLevel = 'Good';
      riskLevel = 'Medium';
    } else if (adequacyRatio >= 1) {
      adequacyLevel = 'Adequate';
      riskLevel = 'Medium';
    } else {
      adequacyLevel = 'Insufficient';
      riskLevel = 'High';
    }

    const analysis = {
      totalCost,
      bankBalance,
      adequacyRatio,
      adequacyLevel,
      riskLevel,
      monthlyExpenses: totalCost / 12,
      coveragePeriod: Math.floor(bankBalance / (totalCost / 12)),
      recommendations: []
    };

    if (adequacyRatio < 1.2) {
      analysis.recommendations.push('Consider additional funding sources or scholarships');
    }
    if (!financial.fundingSource) {
      analysis.recommendations.push('Clearly document your primary funding source');
    }
    if (financial.fundingSource === 'Personal/Family Funds' && !financial.sponsorInfo?.name) {
      analysis.recommendations.push('Provide complete sponsor information and affidavit');
    }

    setFinancialAnalysis(analysis);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Financial Capability Review</h3>
        <p className="text-gray-400 text-lg">Comprehensive analysis of your financial preparedness</p>
      </div>

      {/* Financial Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="text-3xl font-bold text-blue-400 mb-2">
            ${parseInt(financialAnalysis.totalCost || 0).toLocaleString()}
          </div>
          <div className="text-gray-400">Annual Education Cost</div>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="text-3xl font-bold text-blue-400 mb-2">
            ${parseInt(financialAnalysis.bankBalance || 0).toLocaleString()}
          </div>
          <div className="text-gray-400">Available Funds</div>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className={`text-3xl font-bold mb-2 ${
            financialAnalysis.adequacyLevel === 'Excellent' || financialAnalysis.adequacyLevel === 'Very Good' ? 'text-emerald-400' :
            financialAnalysis.adequacyLevel === 'Good' || financialAnalysis.adequacyLevel === 'Adequate' ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {financialAnalysis.adequacyLevel}
          </div>
          <div className="text-gray-400">Financial Adequacy</div>
        </div>
      </div>

      {/* Detailed Analysis */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-6">📊 Financial Analysis Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="text-lg font-medium text-white mb-4">Coverage Analysis</h5>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-300">Coverage Ratio:</span>
                <span className="text-blue-400 font-bold">
                  {(financialAnalysis.adequacyRatio || 0).toFixed(2)}x
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Monthly Expenses:</span>
                <span className="text-blue-400 font-bold">
                  ${parseInt(financialAnalysis.monthlyExpenses || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Coverage Period:</span>
                <span className="text-blue-400 font-bold">
                  {financialAnalysis.coveragePeriod || 0} months
                </span>
              </div>
            </div>
          </div>
          <div>
            <h5 className="text-lg font-medium text-white mb-4">Risk Assessment</h5>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-300">Financial Risk:</span>
                <span className={`font-bold ${
                  financialAnalysis.riskLevel === 'Low' ? 'text-emerald-400' :
                  financialAnalysis.riskLevel === 'Medium' ? 'text-yellow-400' :
                  'text-red-400'
                }`}>
                  {financialAnalysis.riskLevel}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">Funding Source:</span>
                <span className="text-blue-400">{formData.financialDetails?.fundingSource || 'Not specified'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {financialAnalysis.recommendations && financialAnalysis.recommendations.length > 0 && (
        <div className="bg-yellow-900/20 border border-yellow-400 p-6 rounded-lg">
          <h4 className="text-yellow-300 font-semibold mb-4">💡 Financial Recommendations</h4>
          <ul className="space-y-2">
            {financialAnalysis.recommendations.map((rec, index) => (
              <li key={index} className="text-yellow-200 flex items-start">
                <span className="text-yellow-400 mr-2">•</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Academic Analysis
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors"
        >
          Continue to Final Check →
        </button>
      </div>
    </div>
  );
};

// Student Visa Step 7: Final Documentation Check
const StudentVisaStep7FinalCheck = ({ formData, setFormData, onNext, onBack }) => {
  const [finalScore, setFinalScore] = useState(0);

  React.useEffect(() => {
    calculateFinalScore();
  }, []);

  const calculateFinalScore = () => {
    const analysis = formData.analysisResults || {};
    let score = analysis.overall_score || 0;

    // Only add bonus if actual requirements are met
    const requiredDocsCheck = checkRequiredDocuments();
    const schoolInfoComplete = checkSchoolInformation();
    const financialComplete = checkFinancialRequirements();

    if (schoolInfoComplete) score += 5;
    if (financialComplete) score += 5;
    if (requiredDocsCheck.percentage >= 80) score += 5;

    const finalCalculatedScore = Math.max(0, Math.min(100, Math.round(score)));
    setFinalScore(finalCalculatedScore);
    setFormData({ ...formData, finalScore: finalCalculatedScore });
  };

  // Check for specific required documents based on visa type
  const checkRequiredDocuments = () => {
    const documents = formData.documents || [];
    const visaType = formData.visaType || '';
    
    let requiredDocs = [];
    
    // Define required documents based on visa type
    if (visaType === 'f1') {
      requiredDocs = [
        'I-20', 'SEVIS', 'Passport', 'Transcripts', 'Degree', 'Financial Statement', 'Bank Statement', 'TOEFL'
      ];
    } else if (visaType === 'j1') {
      requiredDocs = [
        'DS-2019', 'SEVIS', 'Passport', 'CV', 'Research Proposal', 'Financial Support', 'Sponsor Letter'
      ];
    } else if (visaType === 'm1') {
      requiredDocs = [
        'I-20', 'SEVIS', 'Passport', 'Training Program Details', 'Financial Statement', 'Bank Statement'
      ];
    } else {
      requiredDocs = ['Passport', 'Financial Statement', 'Academic Documents'];
    }

    const foundDocs = requiredDocs.map(reqDoc => {
      const found = documents.some(doc => 
        doc.name && doc.name.toLowerCase().includes(reqDoc.toLowerCase())
      );
      return { doc: reqDoc, found };
    });

    const foundCount = foundDocs.filter(d => d.found).length;
    const percentage = requiredDocs.length > 0 ? (foundCount / requiredDocs.length) * 100 : 0;

    return { foundCount, totalRequired: requiredDocs.length, percentage, details: foundDocs };
  };

  // Check school information completeness
  const checkSchoolInformation = () => {
    const school = formData.schoolDetails || {};
    const requiredFields = ['schoolName', 'programName', 'startDate'];
    const completed = requiredFields.filter(field => school[field] && school[field].trim() !== '').length;
    return completed === requiredFields.length;
  };

  // Check financial requirements
  const checkFinancialRequirements = () => {
    const financial = formData.financialDetails || {};
    const totalCost = parseFloat(financial.totalCost || 0);
    const bankBalance = parseFloat(financial.bankBalance || 0);
    return financial.fundingSource && totalCost > 0 && bankBalance > 0;
  };

  // Check academic information completeness
  const checkAcademicInformation = () => {
    const academic = formData.academicDetails || {};
    return academic.previousEducation && academic.previousEducation !== '';
  };

  const requiredDocsCheck = checkRequiredDocuments();
  const schoolInfoComplete = checkSchoolInformation();
  const financialComplete = checkFinancialRequirements();
  const academicComplete = checkAcademicInformation();

  const checklistItems = [
    {
      id: 'visa_type',
      text: 'Student visa type selected',
      checked: !!formData.visaType,
      required: true,
      details: formData.visaType ? `Selected: ${formData.visaType.toUpperCase()}` : 'No visa type selected'
    },
    {
      id: 'school_info',
      text: 'School and program information completed',
      checked: schoolInfoComplete,
      required: true,
      details: schoolInfoComplete ? 
        `${formData.schoolDetails?.schoolName || 'School'} - ${formData.schoolDetails?.programName || 'Program'}` :
        'Missing school name, program name, or start date'
    },
    {
      id: 'academic_info',
      text: 'Academic background information provided',
      checked: academicComplete,
      required: true,
      details: academicComplete ? 
        `Previous education: ${formData.academicDetails?.previousEducation}` :
        'Academic background information incomplete'
    },
    {
      id: 'financial_docs',
      text: 'Financial information documented',
      checked: financialComplete,
      required: true,
      details: financialComplete ? 
        `Funding: ${formData.financialDetails?.fundingSource}, Cost: $${formData.financialDetails?.totalCost}` :
        'Missing funding source, program cost, or bank balance'
    },
    {
      id: 'documents_uploaded',
      text: `Required documents uploaded (${requiredDocsCheck.foundCount}/${requiredDocsCheck.totalRequired})`,
      checked: requiredDocsCheck.percentage >= 70,
      required: true,
      details: requiredDocsCheck.percentage >= 70 ? 
        `${requiredDocsCheck.foundCount} of ${requiredDocsCheck.totalRequired} required documents found` :
        `Only ${requiredDocsCheck.foundCount} of ${requiredDocsCheck.totalRequired} required documents uploaded`
    },
    {
      id: 'analysis_complete',
      text: 'Academic analysis completed',
      checked: !!formData.analysisResults?.overall_score,
      required: true,
      details: formData.analysisResults?.overall_score ? 
        `Analysis score: ${formData.analysisResults.overall_score}%` :
        'Academic analysis not yet completed'
    },
    {
      id: 'adequate_funding',
      text: 'Adequate financial support demonstrated',
      checked: (() => {
        const financial = formData.financialDetails || {};
        const totalCost = parseFloat(financial.totalCost || 0);
        const bankBalance = parseFloat(financial.bankBalance || 0);
        return bankBalance >= totalCost && totalCost > 0;
      })(),
      required: false,
      details: (() => {
        const financial = formData.financialDetails || {};
        const totalCost = parseFloat(financial.totalCost || 0);
        const bankBalance = parseFloat(financial.bankBalance || 0);
        if (totalCost === 0) return 'Program cost not specified';
        if (bankBalance === 0) return 'Bank balance not provided';
        if (bankBalance >= totalCost) return `Bank balance ($${bankBalance.toLocaleString()}) covers program cost ($${totalCost.toLocaleString()})`;
        return `Bank balance ($${bankBalance.toLocaleString()}) is less than program cost ($${totalCost.toLocaleString()})`;
      })()
    }
  ];

  const requiredItems = checklistItems.filter(item => item.required);
  const completedRequired = requiredItems.filter(item => item.checked).length;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Final Documentation Check</h3>
        <p className="text-gray-400 text-lg">Review your application before generating the student visa packet</p>
      </div>

      {/* Final Score */}
      <div className="bg-gradient-to-r from-blue-900/20 to-gray-900/20 p-8 rounded-lg border border-blue-400/30">
        <div className="text-center">
          <div className="text-6xl font-bold text-blue-400 mb-2">{finalScore}%</div>
          <div className="text-xl text-white mb-4">Final Application Strength</div>
          <div className={`text-lg ${
            finalScore >= 80 ? 'text-emerald-400' :
            finalScore >= 60 ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {finalScore >= 80 ? 'Excellent - Ready for submission' :
             finalScore >= 60 ? 'Good - Minor improvements possible' :
             'Needs improvement before submission'}
          </div>
        </div>
      </div>

      {/* Readiness Checklist */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">Application Readiness Checklist</h4>
        <div className="space-y-4">
          {checklistItems.map((item) => (
            <div key={item.id} className="p-4 bg-gray-700 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-1">
                  <div className={`w-6 h-6 rounded border-2 mr-4 flex items-center justify-center mt-1 ${
                    item.checked ? 'bg-emerald-600 border-emerald-600' : 'border-red-500 bg-red-500/20'
                  }`}>
                    {item.checked ? (
                      <span className="text-white text-sm">✓</span>
                    ) : (
                      <span className="text-red-400 text-sm">✗</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className={`font-medium ${item.checked ? 'text-white' : 'text-gray-300'}`}>
                        {item.text}
                      </span>
                      {item.required && (
                        <span className="ml-2 text-red-400 text-xs font-bold">*REQUIRED</span>
                      )}
                    </div>
                    <div className={`text-sm mt-1 ${
                      item.checked ? 'text-emerald-300' : 'text-yellow-300'
                    }`}>
                      {item.details}
                    </div>
                  </div>
                </div>
                <span className={`text-sm font-medium px-3 py-1 rounded ${
                  item.checked 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-red-500 text-white'
                }`}>
                  {item.checked ? 'PASS' : 'FAIL'}
                </span>
              </div>
            </div>
          ))}
        </div>
        
        {/* Summary Statistics */}
        <div className="mt-6 p-4 bg-gray-900 rounded-lg">
          <h5 className="text-white font-medium mb-3">Checklist Summary</h5>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-2xl font-bold text-emerald-400">
                {checklistItems.filter(item => item.checked && item.required).length}/{requiredItems.length}
              </div>
              <div className="text-gray-400 text-sm">Required Items Complete</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">
                {checklistItems.filter(item => item.checked).length}/{checklistItems.length}
              </div>
              <div className="text-gray-400 text-sm">Total Items Complete</div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Document Requirements */}
      {requiredDocsCheck.percentage < 100 && (
        <div className="bg-yellow-900/20 border border-yellow-400 p-6 rounded-lg">
          <h4 className="text-yellow-300 font-semibold mb-4">📋 Missing Required Documents</h4>
          <p className="text-yellow-200 mb-4">
            The following documents are required for a {formData.visaType?.toUpperCase() || 'student'} visa application:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {requiredDocsCheck.details.map((docCheck, index) => (
              <div key={index} className={`p-3 rounded flex items-center ${
                docCheck.found ? 'bg-emerald-900/30 border border-emerald-400' : 'bg-red-900/30 border border-red-400'
              }`}>
                <span className={`w-5 h-5 rounded-full mr-3 flex items-center justify-center text-xs ${
                  docCheck.found ? 'bg-emerald-600 text-white' : 'bg-red-500 text-white'
                }`}>
                  {docCheck.found ? '✓' : '✗'}
                </span>
                <span className={docCheck.found ? 'text-emerald-200' : 'text-red-200'}>
                  {docCheck.doc}
                </span>
                {!docCheck.found && (
                  <span className="ml-auto text-red-300 text-xs">MISSING</span>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-blue-900/20 border border-blue-400 rounded">
            <p className="text-blue-200 text-sm">
              💡 <strong>Tip:</strong> Upload documents in Step 4 (Supporting Evidence Upload) to improve your application completeness.
            </p>
          </div>
        </div>
      )}

      {/* Application Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-400 mb-2">
            {formData.visaType?.toUpperCase() || 'N/A'}
          </div>
          <div className="text-gray-400 text-sm">Visa Type</div>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-400 mb-2">
            {requiredDocsCheck.foundCount}/{requiredDocsCheck.totalRequired}
          </div>
          <div className="text-gray-400 text-sm">Required Docs</div>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-400 mb-2">
            {completedRequired}/{requiredItems.length}
          </div>
          <div className="text-gray-400 text-sm">Required Items</div>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-400 mb-2">
            ${parseInt(formData.financialDetails?.totalCost || 0).toLocaleString()}
          </div>
          <div className="text-gray-400 text-sm">Total Cost</div>
        </div>
      </div>

      {/* Proceed Message */}
      <div className="bg-gray-700/50 border border-gray-600 p-4 rounded-lg">
        <div className="flex items-center">
          <span className="text-blue-400 mr-2">ℹ️</span>
          <p className="text-gray-300 text-sm">
            <strong>You can proceed to packet generation regardless of completion status.</strong> 
            The system will generate a comprehensive packet with available information and provide guidance on any missing items.
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Financial Review
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors"
        >
          Generate Student Visa Packet →
        </button>
      </div>
    </div>
  );
};

// Student Visa Step 8: Generate Student Visa Package
const StudentVisaStep8GeneratePacket = ({ formData, setFormData, onBack, onClose }) => {
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [packet, setPacket] = useState(formData.packet || null);

  const generatePacket = async () => {
    setGenerating(true);
    
    try {
      // Real API call to generate student visa packet
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      // Format the request to match backend expectations for student visa
      const petitionerDetails = {
        fullName: formData.schoolDetails?.schoolName || "Educational Institution",
        status: "sevp-approved-school",
        employment: "Educational Institution",
        income: formData.financialDetails?.totalCost || "50000",
        address: formData.schoolDetails?.address || "School Address"
      };
      
      const beneficiaryDetails = {
        fullName: "Student Name",
        status: `${formData.visaType || 'f1'}-student`,
        country: "Home Country",
        location: "Current Location"
      };
      
      const relationshipDetails = {
        visaType: formData.visaType || 'f1',
        programName: formData.schoolDetails?.programName || "Academic Program",
        programLevel: formData.schoolDetails?.programLevel || "undergraduate",
        startDate: formData.schoolDetails?.startDate || new Date().toISOString().split('T')[0],
        totalCost: formData.financialDetails?.totalCost || "50000",
        fundingSource: formData.financialDetails?.fundingSource || "Personal Funds",
        education: formData.academicDetails?.previousEducation || "high-school"
      };
      
      const formattedDocuments = (formData.documents || []).map(doc => {
        // Create a copy without the file object for JSON serialization
        const { file, ...docWithoutFile } = doc;
        return {
          name: doc.name || "Document",
          category: doc.category || "academic",
          date: doc.timestamp?.split('T')[0] || new Date().toISOString().split('T')[0],
          // Include OCR analysis results
          content_description: doc.content_description,
          case_relevance: doc.case_relevance,
          exhibit_description: doc.exhibit_description,
          immigration_value: doc.immigration_value,
          parties_mentioned: doc.parties_mentioned,
          key_dates: doc.key_dates,
          financial_amounts: doc.financial_amounts,
          red_flags: doc.red_flags,
          authenticity_indicators: doc.authenticity_indicators,
          extracted_text: doc.extracted_text,
          analysis: doc.analysis
        };
      });
      
      const response = await fetch(`${backendUrl}/api/ai/generate-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          case_type: `${(formData.visaType || 'F1').toUpperCase()} Student Visa`,
          petitioner_details: petitionerDetails,
          beneficiary_details: beneficiaryDetails,
          relationship_details: relationshipDetails,
          documents: formattedDocuments,
          analysis_results: formData.analysisResults || {},
          final_score: formData.finalScore || 50
        })
      });

      if (response.ok) {
        const result = await response.json();
        const enhancedResult = {
          ...result,
          contents: result.package_contents || result.contents || [
            'Student Visa Application Cover Letter.docx',
            'Academic Program Documentation.docx',
            'Financial Support Evidence Guide.docx',
            'Required Forms Checklist.docx',
            'Supporting Documentation Guide.docx',
            'Interview Preparation Guide.docx',
            'SEVIS and I-20 Instructions.docx',
            'Post-Arrival Information.docx'
          ],
          id: result.package_id || result.id || `SV-${Date.now()}`,
          downloadUrl: result.download_url || result.downloadUrl || `${backendUrl}/api/download-package/${result.package_id || result.id || `SV-${Date.now()}`}`,
          score: formData.finalScore || 0,
          title: `${formData.visaType?.toUpperCase() || 'Student'} Visa Application Package`,
          created: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        };
        setPacket(enhancedResult);
        setFormData({ ...formData, packet: enhancedResult });
      } else {
        // Fallback to simulated packet generation
        const simulatedPacket = generateStudentVisaPacket(formData);
        setPacket(simulatedPacket);
        setFormData({ ...formData, packet: simulatedPacket });
      }
    } catch (error) {
      console.error('Packet generation error:', error);
      // Fallback to simulated packet generation
      const simulatedPacket = generateStudentVisaPacket(formData);
      setPacket(simulatedPacket);
      setFormData({ ...formData, packet: simulatedPacket });
    }
    
    setGenerating(false);
  };

  const generateStudentVisaPacket = (data) => {
    const visaTypeNames = {
      'f1': 'F-1 Academic Student',
      'j1': 'J-1 Exchange Visitor',
      'm1': 'M-1 Vocational Student'
    };

    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

    return {
      id: `SV-${Date.now()}`,
      type: 'student-visa-packet',
      title: `${visaTypeNames[data.visaType] || 'Student'} Visa Application Package`,
      created: new Date().toISOString(),
      score: data.finalScore || 0,
      contents: [
        'Student Visa Application Cover Letter.docx',
        'Academic Program Documentation.docx',
        'Financial Support Evidence Guide.docx',
        'Required Forms Checklist.docx',
        'Supporting Documentation Guide.docx',
        'Interview Preparation Guide.docx',
        'SEVIS and I-20 Instructions.docx',
        'Post-Arrival Information.docx'
      ],
      downloadUrl: `${backendUrl}/api/download-package/SV-${Date.now()}`,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    };
  };

  const downloadPacket = async () => {
    if (packet && !downloading) {
      setDownloading(true);
      
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        
        // Check if this is a real package (from API) or simulated package
        const isRealPacket = packet.downloadUrl && packet.downloadUrl.includes('/api/download-package/') && !packet.downloadUrl.includes('SV-');
        const isFromAPI = packet.package_id || (packet.id && !packet.id.startsWith('SV-'));
        
        if (isFromAPI || isRealPacket) {
          // Real package from API - use the provided download URL
          const downloadUrl = packet.downloadUrl || `${backendUrl}/api/download-package/${packet.package_id || packet.id}`;
          
          const response = await fetch(downloadUrl);
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `student-visa-packet-${packet.package_id || packet.id}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setDownloading(false);
            return;
          }
        }
        
        // Fallback: Generate package on-demand
        const generateResponse = await fetch(`${backendUrl}/api/ai/generate-package`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            case_type: `${(formData.visaType || 'F1').toUpperCase()} Student Visa`,
            petitioner_details: {
              fullName: formData.schoolDetails?.schoolName || "Educational Institution",
              status: "sevp-approved-school",
              employment: "Educational Institution",
              income: formData.financialDetails?.totalCost || "50000",
              address: formData.schoolDetails?.address || "School Address"
            },
            beneficiary_details: {
              fullName: "Student Name",
              status: `${formData.visaType || 'f1'}-student`,
              country: "Home Country",
              location: "Current Location"
            },
            relationship_details: {
              visaType: formData.visaType || 'f1',
              programName: formData.schoolDetails?.programName || "Academic Program",
              programLevel: formData.schoolDetails?.programLevel || "undergraduate",
              startDate: formData.schoolDetails?.startDate || new Date().toISOString().split('T')[0],
              totalCost: formData.financialDetails?.totalCost || "50000",
              fundingSource: formData.financialDetails?.fundingSource || "Personal Funds",
              education: formData.academicDetails?.previousEducation || "high-school"
            },
            documents: (formData.documents || []).map(doc => ({
              name: doc.name || "Document",
              category: doc.category || "academic",
              date: doc.timestamp?.split('T')[0] || new Date().toISOString().split('T')[0]
            })),
            analysis_results: formData.analysisResults || {},
            final_score: formData.finalScore || 50
          })
        });

        if (generateResponse.ok) {
          const generateResult = await generateResponse.json();
          const realDownloadUrl = generateResult.download_url || `${backendUrl}/api/download-package/${generateResult.package_id}`;
          
          const downloadResponse = await fetch(realDownloadUrl);
          if (downloadResponse.ok) {
            const blob = await downloadResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `student-visa-packet-${generateResult.package_id}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            // Update the packet data with the real package info
            const updatedPacket = {
              ...packet,
              id: generateResult.package_id,
              package_id: generateResult.package_id,
              downloadUrl: realDownloadUrl
            };
            setPacket(updatedPacket);
            setFormData({ ...formData, packet: updatedPacket });
            setDownloading(false);
            return;
          } else {
            throw new Error(`Download failed: ${downloadResponse.status} ${downloadResponse.statusText}`);
          }
        } else {
          const errorText = await generateResponse.text();
          console.error('Package generation failed:', errorText);
          throw new Error(`Package generation failed: ${generateResponse.status} - ${errorText}`);
        }
        
      } catch (error) {
        console.error('Download error:', error);
        console.log('Form data at error:', formData);
        console.log('Packet data at error:', packet);
        
        const errorMessage = error.message || 'Unable to generate package';
        alert(`Download failed: ${errorMessage}\n\nThis might be due to:\n1. Network connectivity issues\n2. Server maintenance\n3. Invalid case data\n\nPlease try again in a few moments or contact support if the issue persists.`);
        
        // Try one more fallback
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        try {
          window.open(`${backendUrl}/api/download-package/${packet.package_id || packet.id}`, '_blank');
        } catch (fallbackError) {
          console.error('Final fallback also failed:', fallbackError);
        }
      } finally {
        setDownloading(false);
      }
    }
  };

  React.useEffect(() => {
    if (!packet) {
      generatePacket();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Student Visa Packet Generated</h3>
        <p className="text-gray-400 text-lg">Your comprehensive student visa application package is ready</p>
      </div>

      {generating && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Generating Your Student Visa Packet</h4>
          <p className="text-gray-400">Creating professional documents and organizing your evidence...</p>
        </div>
      )}

      {!generating && packet && (
        <>
          {/* Success Message */}
          <div className="bg-emerald-900/20 border border-emerald-400 p-8 rounded-lg text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-3xl font-bold text-emerald-300 mb-4">Packet Ready!</h3>
            <p className="text-emerald-200 text-lg mb-6">
              Your student visa application package has been generated and is ready for download.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-emerald-300 font-medium">
              <div>Package ID: {packet.id}</div>
              <div>Final Score: {packet.score}%</div>
              <div>Type: {formData.visaType?.toUpperCase()}</div>
            </div>
          </div>

          {/* Package Contents */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📦 Package Contents (All Word Documents)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {packet.contents.map((item, index) => (
                <div key={index} className="flex items-center bg-gray-700 p-3 rounded">
                  <span className="text-blue-400 mr-3">📝</span>
                  <span className="text-white text-sm">{item}</span>
                  <span className="text-blue-300 text-xs ml-auto">.docx</span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-900/20 border border-blue-400 rounded">
              <p className="text-blue-200 text-sm">
                <strong>📝 All documents are in Microsoft Word format</strong> - Ready for editing and customization
              </p>
            </div>
          </div>

          {/* Download Section */}
          <div className="bg-gray-800 p-6 rounded-lg text-center">
            <h4 className="text-xl font-semibold text-white mb-4">📥 Download Your Complete Package</h4>
            <div className="mb-4">
              <p className="text-gray-300 text-sm mb-2">ZIP file containing all Word documents</p>
              <p className="text-gray-400 text-xs">Professional templates ready for customization</p>
            </div>
            <button
              onClick={downloadPacket}
              disabled={downloading}
              className={`px-8 py-4 rounded-lg font-bold text-lg transition-colors mb-4 flex items-center justify-center mx-auto ${
                downloading 
                  ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {downloading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-transparent mr-2"></div>
                  Preparing Download...
                </>
              ) : (
                <>
                  <span className="mr-2">📥</span>
                  Download Student Visa Packet (.zip)
                </>
              )}
            </button>
            <div className="space-y-2">
              <div className="text-gray-400 text-sm">
                Package ID: <span className="text-blue-400 font-mono">{packet.id}</span>
              </div>
              <div className="text-gray-400 text-sm">
                Package expires: {new Date(packet.expiresAt).toLocaleDateString()}
              </div>
              <div className="text-emerald-400 text-sm">
                ✅ All documents are editable Word files (.docx)
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
            <h4 className="text-blue-300 font-semibold mb-4">📋 Next Steps</h4>
            <div className="space-y-3 text-blue-200">
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">1.</span>
                <span>Download and review all documents in your packet</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">2.</span>
                <span>Complete and submit DS-160 form online</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">3.</span>
                <span>Schedule visa interview appointment</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">4.</span>
                <span>Prepare for visa interview using provided guide</span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-400 mr-2 font-bold">5.</span>
                <span>Attend visa interview with required documents</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Final Check
        </button>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg font-medium bg-emerald-400 text-black hover:bg-emerald-300 transition-colors"
        >
          Complete & Close
        </button>
      </div>
    </div>
  );
};

// US Citizenship Agent Flow
export const CitizenshipAgentFlow = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    eligibilityPath: '',
    currentStatus: '',
    residenceDetails: {},
    backgroundDetails: {},
    travelHistory: [],
    documents: [],
    analysisResults: {},
    recommendations: [],
    finalScore: 0,
    package: null,
    eligibilityVerification: {},
    readinessScore: 0
  });

  const steps = [
    'Welcome + Eligibility Screening',
    'Detailed Background Assessment',
    'Document Collection & Upload',
    'Travel History Analysis',
    'Background Check & Complication Review',
    'Document Quality Enhancement',
    'Final Eligibility Verification',
    'Generate Naturalization Package'
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-3xl mr-4">🇺🇸</span>
              <div>
                <h2 className="text-2xl font-bold text-white">US Citizenship Agent</h2>
                <p className="text-gray-400">Naturalization Application Process</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Step {currentStep + 1} of {steps.length}</span>
              <span className="text-sm text-gray-400">{Math.round((currentStep / (steps.length - 1)) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
              ></div>
            </div>
            <p className="text-white font-medium mt-2">{steps[currentStep]}</p>
          </div>

          {/* Step Content */}
          {currentStep === 0 && (
            <CitizenshipStep1EligibilityScreening 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
            />
          )}
          {currentStep === 1 && (
            <CitizenshipStep2BackgroundAssessment 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 2 && (
            <CitizenshipStep3DocumentUpload 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 3 && (
            <CitizenshipStep4TravelHistory 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 4 && (
            <CitizenshipStep5BackgroundReview 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 5 && (
            <CitizenshipStep6DocumentEnhancement 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 6 && (
            <CitizenshipStep7FinalVerification 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 7 && (
            <CitizenshipStep8GeneratePackage 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// DACA/TPS Renewal Agent Flow
export const DacaTpsRenewalAgentFlow = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    categoryType: '',
    renewalDetails: {
      currentStatus: '',
      expirationDate: '',
      previousApprovals: '',
      travelHistory: '',
      criminalHistory: ''
    },
    eligibilityDetails: {
      continuousResidence: false,
      physicalPresence: false,
      noConvictions: false,
      educationRequirement: false
    },
    documents: [],
    analysisResults: {},
    affidavitContent: '',
    explanationLetters: [],
    packetComponents: [],
    recommendations: [],
    finalScore: 0,
    downloadableTemplates: []
  });

  const steps = [
    'Select Your Category',
    'Confirm Renewal Window & Eligibility',
    'Upload Required Documents',
    'AI Evidence Check & Risk Analyzer',
    'Affidavit / Explanation Builder',
    'Final Packet Preparation',
    'Generate Renewal Packet',
    'Final Reminders'
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-3xl mr-4">🔄</span>
              <div>
                <h2 className="text-2xl font-bold text-white">DACA / TPS Renewal Agent</h2>
                <p className="text-gray-400">Status Renewal Application Process</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Step {currentStep + 1} of {steps.length}</span>
              <span className="text-sm text-gray-400">{Math.round((currentStep / (steps.length - 1)) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
              ></div>
            </div>
            <p className="text-white font-medium mt-2">{steps[currentStep]}</p>
          </div>

          {/* Step Content */}
          {currentStep === 0 && (
            <DacaStep1CategorySelection 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
            />
          )}
          {currentStep === 1 && (
            <DacaStep2EligibilityCheck 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 2 && (
            <DacaStep3DocumentUpload 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 3 && (
            <DacaStep4EvidenceAnalysis 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 4 && (
            <DacaStep5AffidavitBuilder 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 5 && (
            <DacaStep6PacketPreparation 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 6 && (
            <DacaStep7GeneratePacket 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 7 && (
            <DacaStep8FinalReminders 
              formData={formData} 
              setFormData={setFormData} 
              onBack={handleBack} 
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Visitor & Tourist Visa Agent Flow
export const VisitorTouristAgentFlow = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    visaType: '',
    purposeOfVisit: '',
    personalDetails: {},
    travelHistory: {},
    documents: [],
    analysisResults: {},
    preparationProgress: {},
    package: null,
    recommendations: [],
    finalScore: 0
  });

  const steps = [
    'Visa Type & Purpose Selection',
    'Personal Information & Travel History',
    'Document Upload',
    'AI Application Analysis & Risk Assessment',
    'Interview Preparation & Tips',
    'Generate Application Package',
    'Final Guidance & Next Steps'
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <span className="text-3xl mr-4">✈️</span>
              <div>
                <h2 className="text-2xl font-bold text-white">Visitor & Tourist Visa Agent</h2>
                <p className="text-gray-400">B-1/B-2 & ESTA Visa Application Process</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Step {currentStep + 1} of {steps.length}</span>
              <span className="text-sm text-gray-400">{Math.round((currentStep / (steps.length - 1)) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
              ></div>
            </div>
            <p className="text-white font-medium mt-2">{steps[currentStep]}</p>
          </div>

          {/* Step Content */}
          {currentStep === 0 && (
            <VisitorStep1VisaTypeSelection 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
            />
          )}
          {currentStep === 1 && (
            <VisitorStep2PersonalDetails 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 2 && (
            <VisitorStep3DocumentUpload 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 3 && (
            <VisitorStep4ApplicationAnalysis 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 4 && (
            <VisitorStep5InterviewPreparation 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 5 && (
            <VisitorStep6GeneratePackage 
              formData={formData} 
              setFormData={setFormData} 
              onNext={handleNext} 
              onBack={handleBack} 
            />
          )}
          {currentStep === 6 && (
            <VisitorStep7FinalGuidance 
              formData={formData} 
              setFormData={setFormData} 
              onBack={handleBack} 
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// RFE/NOID Response Agent Flow - Import from separate file
export { RfeNoidResponseAgentFlow } from './RfeAgentFlow';

// Work Visa Step 4: Smart Evidence Analysis
const WorkVisaStep4Analysis = ({ formData, setFormData, onNext, onBack }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(formData.analysisResults || {});

  const performAnalysis = async () => {
    setAnalyzing(true);
    
    try {
      // First try sophisticated content analysis
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const contentAnalysisResponse = await fetch(`${backendUrl}/api/ai/analyze-content-quality`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_type: 'work-visa',
          case_narrative: generateWorkVisaCaseNarrative(formData),
          documents: formData.documents || [],
          petitioner_details: formData.employerDetails || {},
          beneficiary_details: formData.employeeDetails || {},
          relationship_details: formData.jobDetails || {},
          case_details: {
            case_type: 'work-visa',
            visa_type: formData.visaType
          }
        })
      });

      if (contentAnalysisResponse.ok) {
        const contentAnalysis = await contentAnalysisResponse.json();
        
        // Convert sophisticated analysis to display format
        const sophisticatedResults = {
          overall_score: contentAnalysis.overall_content_score || 50,
          case_strength: contentAnalysis.overall_content_score >= 80 ? 'Strong' : 
                        contentAnalysis.overall_content_score >= 60 ? 'Moderate' : 'Weak',
          content_quality: contentAnalysis.content_quality || {},
          legal_argument_strength: contentAnalysis.legal_argument_analysis || {},
          evidence_coherence: contentAnalysis.evidence_coherence || {},
          professional_presentation: contentAnalysis.professional_presentation || {},
          strengths: contentAnalysis.content_strengths || ['Content analysis in progress'],
          issues: contentAnalysis.content_weaknesses || ['Analysis optimization needed'],
          recommendations: contentAnalysis.improvement_recommendations || ['Content refinement suggested'],
          attorney_assessment: contentAnalysis.attorney_assessment || 'Professional assessment pending',
          approval_likelihood: contentAnalysis.approval_likelihood || 50,
          case_readiness: contentAnalysis.case_readiness || 'Needs Assessment',
          analysis_type: 'sophisticated_content_analysis'
        };
        
        setAnalysisResults(sophisticatedResults);
        setFormData({ ...formData, analysisResults: sophisticatedResults });
      } else {
        // Fallback to legacy analyze-case endpoint
        const legacyResponse = await fetch(`${backendUrl}/api/ai/analyze-case`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            case_type: 'work-visa',
            petitioner_details: formData.employerDetails || {},
            beneficiary_details: formData.employeeDetails || {},
            relationship_details: formData.jobDetails || {},
            documents: formData.documents || []
          })
        });

        if (legacyResponse.ok) {
          const legacyResult = await legacyResponse.json();
          setAnalysisResults(legacyResult);
          setFormData({ ...formData, analysisResults: legacyResult });
        } else {
          // Final fallback to basic analysis
          const basicAnalysis = generateWorkVisaAnalysis(formData);
          setAnalysisResults(basicAnalysis);
          setFormData({ ...formData, analysisResults: basicAnalysis });
        }
      }
    } catch (error) {
      console.error('Analysis error:', error);
      // Fallback to basic analysis
      const basicAnalysis = generateWorkVisaAnalysis(formData);
      setAnalysisResults(basicAnalysis);
      setFormData({ ...formData, analysisResults: basicAnalysis });
    }
    
    setAnalyzing(false);
  };

  const generateWorkVisaCaseNarrative = (data) => {
    const employer = data.employerDetails || {};
    const employee = data.employeeDetails || {};
    const job = data.jobDetails || {};
    
    return `
WORK VISA CASE NARRATIVE:

EMPLOYER PROFILE:
${employer.companyName || 'Company Name Not Provided'} is a ${employer.industry || 'industry not specified'} company 
with ${employer.companySize || 'unspecified'} employees. The company is located at ${employer.address || 'address not provided'} 
and has been in business for ${employer.yearsInBusiness || 'unspecified'} years. The company's annual revenue is 
${employer.annualRevenue || 'not provided'}.

POSITION DETAILS:
The position of ${job.jobTitle || 'job title not specified'} requires ${job.educationRequired || 'education requirements not specified'} 
and ${job.experienceRequired || 'experience requirements not specified'} years of experience. The position will be located in 
${job.workLocation || 'work location not specified'} with an annual salary of ${job.salary || 'salary not specified'}.

Job responsibilities include: ${job.jobDescription || 'job description not provided'}

BENEFICIARY QUALIFICATIONS:
${employee.fullName || 'Employee name not provided'} holds a ${employee.education || 'education not specified'} degree 
in ${employee.fieldOfStudy || 'field not specified'} and has ${employee.experience || 'experience not specified'} years 
of relevant work experience. The beneficiary's current status is ${employee.currentStatus || 'status not specified'} 
and they are currently located in ${employee.currentLocation || 'location not specified'}.

Previous work experience includes: ${employee.workHistory || 'work history not provided'}

VISA TYPE JUSTIFICATION:
This ${data.visaType || 'visa type not specified'} petition is being filed because ${job.visaJustification || 'justification not provided'}.
The position requires specialized knowledge and skills that make it appropriate for this visa category.

SUPPORTING EVIDENCE:
The case includes ${(data.documents || []).length} supporting documents with detailed content analysis:
${(data.documents || []).map(doc => {
  let description = `- ${doc.name} (${doc.category || 'category not specified'})`;
  if (doc.content_description) {
    description += `\n  Content: ${doc.content_description}`;
  }
  if (doc.case_relevance) {
    description += `\n  Relevance: ${doc.case_relevance}`;
  }
  if (doc.immigration_value) {
    description += `\n  Immigration Value: ${doc.immigration_value}`;
  }
  if (doc.parties_mentioned && doc.parties_mentioned.length > 0) {
    description += `\n  Parties Mentioned: ${doc.parties_mentioned.join(', ')}`;
  }
  if (doc.key_dates && doc.key_dates.length > 0) {
    description += `\n  Key Dates: ${doc.key_dates.join(', ')}`;
  }
  return description;
}).join('\n\n')}
    `.trim();
  };

  const generateWorkVisaAnalysis = (data) => {
    const documents = data.documents || [];
    const requiredDocs = ['Offer Letter', 'Support Letter', 'Educational Credentials', 'Resume', 'Experience Letters'];
    const availableDocs = documents.map(doc => doc.name);
    
    // Calculate document completion using OCR analysis
    let documentScore = (documents.length / requiredDocs.length) * 100;
    
    // Boost score based on OCR analysis quality
    const ocrAnalyzedDocs = documents.filter(doc => doc.content_description || doc.case_relevance);
    const highValueDocs = documents.filter(doc => doc.immigration_value === 'High');
    const mediumValueDocs = documents.filter(doc => doc.immigration_value === 'Medium');
    
    if (ocrAnalyzedDocs.length > 0) {
      documentScore += 10; // Bonus for OCR analysis
    }
    
    if (highValueDocs.length > 0) {
      documentScore += highValueDocs.length * 5; // Bonus for high-value documents
    }
    
    if (mediumValueDocs.length > 0) {
      documentScore += mediumValueDocs.length * 3; // Bonus for medium-value documents
    }
    
    // Check for red flags in OCR analysis
    const redFlags = documents.filter(doc => doc.red_flags && doc.red_flags.length > 0);
    if (redFlags.length > 0) {
      documentScore -= redFlags.length * 10; // Penalty for red flags
    }
    
    // Analyze visa type requirements
    let visaSpecificScore = 50;
    const visaRequirements = getVisaRequirements(data.visaType);
    
    if (data.employeeDetails.education) {
      visaSpecificScore += 15;
    }
    if (data.employeeDetails.experience && parseInt(data.employeeDetails.experience) >= 3) {
      visaSpecificScore += 15;
    }
    if (data.jobDetails.salary && parseInt(data.jobDetails.salary.replace(/[^0-9]/g, '')) >= 60000) {
      visaSpecificScore += 10;
    }
    if (data.employerDetails.industry) {
      visaSpecificScore += 10;
    }
    
    const overallScore = Math.min(100, Math.round((documentScore + visaSpecificScore) / 2));
    
    // Generate issues and strengths
    const issues = [];
    const strengths = [];
    
    if (documents.length < 3) {
      issues.push('Insufficient document uploads - need more evidence');
    }
    if (!data.employeeDetails.education || data.employeeDetails.education === '') {
      issues.push('Education level not specified - required for most work visas');
    }
    if (!data.employeeDetails.experience || parseInt(data.employeeDetails.experience) < 2) {
      issues.push('Limited work experience documented');
    }
    
    if (documents.length >= 5) {
      strengths.push('Good document coverage provided');
    }
    if (data.employeeDetails.education === 'masters' || data.employeeDetails.education === 'phd') {
      strengths.push('Advanced degree holder - strong qualification');
    }
    if (data.employeeDetails.experience && parseInt(data.employeeDetails.experience) >= 5) {
      strengths.push('Extensive work experience documented');
    }

    return {
      overall_score: overallScore,
      document_completeness: documentScore,
      visa_alignment: visaSpecificScore,
      strengths: strengths.length > 0 ? strengths : ['Basic case information provided'],
      issues: issues.length > 0 ? issues : ['No major issues identified'],
      missing_documents: requiredDocs.filter(req => 
        !availableDocs.some(avail => avail.toLowerCase().includes(req.toLowerCase().split(' ')[0]))
      ),
      recommendations: generateWorkVisaRecommendations(data)
    };
  };

  const getVisaRequirements = (visaType) => {
    const requirements = {
      'h1b': ['Bachelor\'s degree minimum', 'Specialized occupation', 'LCA filed'],
      'l1': ['1+ years with company abroad', 'Managerial or specialized knowledge role'],
      'o1': ['Extraordinary ability documentation', 'Sustained acclaim evidence'],
      'tn': ['NAFTA eligible profession', 'Canadian or Mexican citizenship'],
      'eb2': ['Advanced degree or exceptional ability', 'Labor certification'],
      'other': ['Visa-specific requirements apply']
    };
    return requirements[visaType] || requirements['other'];
  };

  const generateWorkVisaRecommendations = (data) => {
    const recommendations = [];
    const documents = data.documents || [];
    
    if (documents.length < 5) {
      recommendations.push('Upload additional supporting documents to strengthen your case');
    }
    if (!data.employeeDetails.education) {
      recommendations.push('Complete education information - critical for visa eligibility');
    }
    if (!data.jobDetails.salary) {
      recommendations.push('Include salary information to demonstrate prevailing wage compliance');
    }
    if (!data.employerDetails.industry) {
      recommendations.push('Specify employer industry for better case context');
    }
    
    return recommendations.length > 0 ? recommendations : ['Case appears well-prepared for submission'];
  };

  React.useEffect(() => {
    if (Object.keys(analysisResults).length === 0) {
      performAnalysis();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Smart Evidence Analysis</h3>
        <p className="text-gray-400 text-lg">AI-powered analysis of your work visa case</p>
      </div>

      {analyzing && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-400 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Analyzing Your Case</h4>
          <p className="text-gray-400">Our AI is evaluating your work visa evidence and identifying optimization opportunities...</p>
        </div>
      )}

      {!analyzing && analysisResults.overall_score !== undefined && (
        <>
          {/* Overall Score */}
          <div className="bg-gradient-to-r from-orange-900/20 to-gray-900/20 p-8 rounded-lg border border-orange-400/30">
            <div className="text-center">
              <div className="text-6xl font-bold text-orange-400 mb-2">{analysisResults.overall_score}%</div>
              <div className="text-xl text-white mb-4">Case Strength Score</div>
              <div className="text-gray-300">
                {analysisResults.overall_score >= 80 ? 'Strong case preparation' :
                 analysisResults.overall_score >= 60 ? 'Good foundation, some improvements needed' :
                 'Significant strengthening required'}
              </div>
            </div>
          </div>

          {/* Analysis Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">✅ Case Strengths</h4>
              <ul className="space-y-2">
                {analysisResults.strengths?.map((strength, index) => (
                  <li key={index} className="flex items-start text-emerald-300">
                    <span className="text-emerald-400 mr-2">•</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">⚠️ Areas for Improvement</h4>
              <ul className="space-y-2">
                {analysisResults.issues?.map((issue, index) => (
                  <li key={index} className="flex items-start text-yellow-300">
                    <span className="text-yellow-400 mr-2">•</span>
                    {issue}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Missing Documents */}
          {analysisResults.missing_documents && analysisResults.missing_documents.length > 0 && (
            <div className="bg-red-900/20 border border-red-400 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-red-300 mb-4">📋 Missing Critical Documents</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {analysisResults.missing_documents.map((doc, index) => (
                  <div key={index} className="bg-red-900/30 p-3 rounded">
                    <span className="text-red-200">{doc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detailed Metrics */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-lg font-semibold text-white mb-4">📊 Detailed Analysis</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">Document Completeness</span>
                  <span className="text-orange-400 font-bold">{Math.round(analysisResults.document_completeness)}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-orange-400 h-2 rounded-full"
                    style={{ width: `${analysisResults.document_completeness}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">Visa Alignment</span>
                  <span className="text-orange-400 font-bold">{Math.round(analysisResults.visa_alignment)}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-orange-400 h-2 rounded-full"
                    style={{ width: `${analysisResults.visa_alignment}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Documents
        </button>
        <button
          onClick={() => {
            if (!analyzing && analysisResults.overall_score !== undefined) {
              onNext();
            }
          }}
          disabled={analyzing || analysisResults.overall_score === undefined}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            !analyzing && analysisResults.overall_score !== undefined
              ? 'bg-orange-400 text-black hover:bg-orange-300'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Recommendations →
        </button>
      </div>
    </div>
  );
};

// Work Visa Step 5: Recommendations & Fixes
const WorkVisaStep5Recommendations = ({ formData, setFormData, onNext, onBack }) => {
  const [recommendations, setRecommendations] = useState(formData.recommendations || []);
  const [implementedFixes, setImplementedFixes] = useState([]);

  React.useEffect(() => {
    if (recommendations.length === 0 && formData.analysisResults) {
      const analysisRecs = formData.analysisResults.recommendations || [];
      const prioritizedRecs = generatePrioritizedRecommendations(formData);
      setRecommendations([...analysisRecs, ...prioritizedRecs]);
      setFormData({ ...formData, recommendations: [...analysisRecs, ...prioritizedRecs] });
    }
  }, []);

  const generatePrioritizedRecommendations = (data) => {
    const recs = [];
    const analysis = data.analysisResults || {};
    
    // High priority recommendations
    if (analysis.overall_score < 60) {
      recs.push({
        priority: 'High',
        category: 'Critical',
        title: 'Case Strengthening Required',
        description: 'Your case needs significant improvement before submission',
        action: 'Review all missing documents and complete profile information',
        impact: 'Essential for approval'
      });
    }
    
    if (analysis.missing_documents && analysis.missing_documents.length > 0) {
      recs.push({
        priority: 'High',
        category: 'Documents',
        title: 'Missing Required Documents',
        description: `${analysis.missing_documents.length} critical documents are missing`,
        action: 'Upload all missing documents listed in analysis',
        impact: 'High - Required for case processing'
      });
    }
    
    // Medium priority recommendations
    if (!data.employeeDetails.education) {
      recs.push({
        priority: 'Medium',
        category: 'Profile',
        title: 'Complete Education Information',
        description: 'Education level is critical for work visa eligibility',
        action: 'Go back to Profile step and select your education level',
        impact: 'Medium - Affects visa qualification'
      });
    }
    
    if (!data.jobDetails.salary) {
      recs.push({
        priority: 'Medium',
        category: 'Compliance',
        title: 'Salary Information Missing',
        description: 'Salary helps demonstrate prevailing wage compliance',
        action: 'Add salary information in job details',
        impact: 'Medium - Important for wage verification'
      });
    }
    
    // Low priority recommendations
    if (data.documents && data.documents.length < 7) {
      recs.push({
        priority: 'Low',
        category: 'Enhancement',
        title: 'Additional Supporting Documents',
        description: 'More documents can strengthen your case',
        action: 'Consider uploading optional supporting documents',
        impact: 'Low - Helpful but not required'
      });
    }
    
    return recs;
  };

  const handleImplementFix = (index) => {
    if (!implementedFixes.includes(index)) {
      setImplementedFixes([...implementedFixes, index]);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-400 bg-red-900/20 border-red-400';
      case 'Medium': return 'text-yellow-400 bg-yellow-900/20 border-yellow-400';
      case 'Low': return 'text-blue-400 bg-blue-900/20 border-blue-400';
      default: return 'text-gray-400 bg-gray-900/20 border-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Recommendations & Fixes</h3>
        <p className="text-gray-400 text-lg">Prioritized suggestions to strengthen your work visa case</p>
      </div>

      {/* Recommendations List */}
      <div className="space-y-4">
        {recommendations.map((rec, index) => (
          <div key={index} className={`border p-6 rounded-lg ${getPriorityColor(rec.priority)}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(rec.priority)}`}>
                    {rec.priority} Priority
                  </span>
                  <span className="ml-2 text-gray-400 text-sm">{rec.category}</span>
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">{rec.title}</h4>
                <p className="text-gray-300 mb-3">{rec.description}</p>
                <div className="bg-gray-800/50 p-3 rounded mb-3">
                  <p className="text-sm"><strong>Action:</strong> {rec.action}</p>
                </div>
                <p className="text-sm text-gray-400"><strong>Impact:</strong> {rec.impact}</p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => handleImplementFix(index)}
                  disabled={implementedFixes.includes(index)}
                  className={`px-4 py-2 rounded font-medium transition-colors ${
                    implementedFixes.includes(index)
                      ? 'bg-emerald-600 text-white cursor-default'
                      : 'bg-orange-400 text-black hover:bg-orange-300'
                  }`}
                >
                  {implementedFixes.includes(index) ? '✓ Noted' : 'Mark as Addressed'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">Implementation Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-400 mb-1">
              {recommendations.filter(r => r.priority === 'High').length}
            </div>
            <div className="text-gray-400 text-sm">High Priority</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-400 mb-1">
              {recommendations.filter(r => r.priority === 'Medium').length}
            </div>
            <div className="text-gray-400 text-sm">Medium Priority</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400 mb-1">
              {implementedFixes.length}
            </div>
            <div className="text-gray-400 text-sm">Addressed</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Analysis
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium bg-orange-400 text-black hover:bg-orange-300 transition-colors"
        >
          Continue to Corrections →
        </button>
      </div>
    </div>
  );
};

// Work Visa Step 6: Correction Loop
const WorkVisaStep6Corrections = ({ formData, setFormData, onNext, onBack }) => {
  const [corrections, setCorrections] = useState(formData.corrections || []);
  const [newCorrection, setNewCorrection] = useState({ category: '', description: '', status: 'pending' });

  // Auto-generate corrections based on analysis results
  React.useEffect(() => {
    if (corrections.length === 0 && formData.analysisResults) {
      const autoCorrections = generateAutoCorrections(formData);
      setCorrections(autoCorrections);
      setFormData({ ...formData, corrections: autoCorrections });
    }
  }, []);

  const generateAutoCorrections = (data) => {
    const autoCorrections = [];
    const analysis = data.analysisResults || {};
    const documents = data.documents || [];
    
    // Generate corrections based on missing documents
    if (analysis.missing_documents && analysis.missing_documents.length > 0) {
      analysis.missing_documents.forEach((doc, index) => {
        autoCorrections.push({
          id: `auto-${Date.now()}-${index}`,
          category: 'Document Upload',
          description: `Upload missing required document: ${doc}`,
          status: 'pending',
          timestamp: new Date().toISOString(),
          source: 'auto-generated',
          priority: 'high'
        });
      });
    }
    
    // Generate corrections based on profile gaps
    if (!data.employeeDetails?.education) {
      autoCorrections.push({
        id: `auto-${Date.now()}-education`,
        category: 'Profile Information',
        description: 'Complete education level information - required for visa eligibility assessment',
        status: 'pending',
        timestamp: new Date().toISOString(),
        source: 'auto-generated',
        priority: 'high'
      });
    }
    
    if (!data.jobDetails?.salary) {
      autoCorrections.push({
        id: `auto-${Date.now()}-salary`,
        category: 'Job Details',
        description: 'Add salary information to demonstrate prevailing wage compliance',
        status: 'pending',
        timestamp: new Date().toISOString(),
        source: 'auto-generated',
        priority: 'medium'
      });
    }
    
    if (!data.employerDetails?.industry) {
      autoCorrections.push({
        id: `auto-${Date.now()}-industry`,
        category: 'Employer Information',
        description: 'Specify employer industry for better case context and visa alignment',
        status: 'pending',
        timestamp: new Date().toISOString(),
        source: 'auto-generated',
        priority: 'medium'
      });
    }
    
    // Generate corrections based on document count
    if (documents.length < 3) {
      autoCorrections.push({
        id: `auto-${Date.now()}-docs`,
        category: 'Document Upload',
        description: `Only ${documents.length} document${documents.length !== 1 ? 's' : ''} uploaded - need at least 3-5 supporting documents`,
        status: 'pending',
        timestamp: new Date().toISOString(),
        source: 'auto-generated',
        priority: 'high'
      });
    }
    
    // Generate corrections based on experience
    if (!data.employeeDetails?.experience || parseInt(data.employeeDetails.experience) < 2) {
      autoCorrections.push({
        id: `auto-${Date.now()}-exp`,
        category: 'Profile Information',
        description: 'Add more detailed work experience information to strengthen visa application',
        status: 'pending',
        timestamp: new Date().toISOString(),
        source: 'auto-generated',
        priority: 'medium'
      });
    }
    
    return autoCorrections;
  };

  const correctionCategories = [
    'Profile Information',
    'Document Upload',
    'Job Details',
    'Employer Information',
    'Education Records',
    'Experience Documentation',
    'Other'
  ];

  const handleAddCorrection = () => {
    if (newCorrection.description.trim()) {
      const correction = {
        id: Date.now(),
        ...newCorrection,
        timestamp: new Date().toISOString(),
        source: 'user-added',
        priority: 'medium'
      };
      const updatedCorrections = [...corrections, correction];
      setCorrections(updatedCorrections);
      setFormData({ ...formData, corrections: updatedCorrections });
      setNewCorrection({ category: '', description: '', status: 'pending' });
    }
  };

  const handleCompleteCorrection = (id) => {
    const updatedCorrections = corrections.map(corr => 
      corr.id === id ? { ...corr, status: 'completed' } : corr
    );
    setCorrections(updatedCorrections);
    setFormData({ ...formData, corrections: updatedCorrections });
  };

  const completedCount = corrections.filter(c => c.status === 'completed').length;
  const highPriorityCount = corrections.filter(c => c.priority === 'high' && c.status === 'pending').length;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-400 bg-red-900/20 border-red-400';
      case 'medium': return 'text-yellow-400 bg-yellow-900/20 border-yellow-400';
      case 'low': return 'text-blue-400 bg-blue-900/20 border-blue-400';
      default: return 'text-gray-400 bg-gray-900/20 border-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Correction & Improvement Loop</h3>
        <p className="text-gray-400 text-lg">Address identified issues to strengthen your work visa case</p>
      </div>

      {/* Analysis-Based Corrections Summary */}
      {corrections.filter(c => c.source === 'auto-generated').length > 0 && (
        <div className="bg-orange-900/20 border border-orange-400 p-6 rounded-lg">
          <h4 className="text-orange-300 font-semibold mb-4">🔍 Analysis-Identified Issues</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-400 mb-1">
                {corrections.filter(c => c.priority === 'high' && c.status === 'pending').length}
              </div>
              <div className="text-gray-400 text-sm">High Priority</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400 mb-1">
                {corrections.filter(c => c.priority === 'medium' && c.status === 'pending').length}
              </div>
              <div className="text-gray-400 text-sm">Medium Priority</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-400 mb-1">
                {completedCount}
              </div>
              <div className="text-gray-400 text-sm">Completed</div>
            </div>
          </div>
        </div>
      )}

      {/* Corrections List */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-white">Issues to Address ({completedCount}/{corrections.length} completed)</h4>
        {corrections.length === 0 ? (
          <div className="bg-gray-800 p-8 rounded-lg text-center">
            <div className="text-4xl mb-4">✅</div>
            <p className="text-gray-300">No issues identified - your case looks strong!</p>
          </div>
        ) : (
          corrections.map((correction) => (
            <div key={correction.id} className={`p-4 rounded-lg border-2 transition-all ${
              correction.status === 'completed'
                ? 'border-emerald-400 bg-emerald-900/20'
                : getPriorityColor(correction.priority)
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <span className={`text-sm px-2 py-1 rounded mr-2 ${
                      correction.priority === 'high' ? 'bg-red-900/20 text-red-300' :
                      correction.priority === 'medium' ? 'bg-yellow-900/20 text-yellow-300' :
                      'bg-blue-900/20 text-blue-300'
                    }`}>
                      {correction.category}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      correction.priority === 'high' ? 'bg-red-800 text-red-200' :
                      correction.priority === 'medium' ? 'bg-yellow-800 text-yellow-200' :
                      'bg-blue-800 text-blue-200'
                    }`}>
                      {correction.priority} priority
                    </span>
                    {correction.source === 'auto-generated' && (
                      <span className="text-xs px-2 py-1 rounded bg-purple-800 text-purple-200 ml-2">
                        AI Identified
                      </span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded ml-2 ${
                      correction.status === 'completed'
                        ? 'bg-emerald-900/20 text-emerald-300'
                        : 'bg-orange-900/20 text-orange-300'
                    }`}>
                      {correction.status}
                    </span>
                  </div>
                  <p className="text-white mb-2">{correction.description}</p>
                  <p className="text-gray-400 text-xs">
                    {correction.source === 'auto-generated' ? 'Identified by AI analysis' : 'Added manually'} • 
                    {new Date(correction.timestamp).toLocaleDateString()}
                  </p>
                </div>
                {correction.status === 'pending' && (
                  <button
                    onClick={() => handleCompleteCorrection(correction.id)}
                    className="ml-4 px-3 py-1 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 transition-colors"
                  >
                    Mark Complete
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add New Correction */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">Add Additional Correction</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-gray-300 mb-2">Category</label>
            <select
              value={newCorrection.category}
              onChange={(e) => setNewCorrection({ ...newCorrection, category: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-orange-400"
            >
              <option value="">Select Category</option>
              {correctionCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-300 mb-2">Description</label>
            <input
              type="text"
              placeholder="Describe the correction or improvement needed"
              value={newCorrection.description}
              onChange={(e) => setNewCorrection({ ...newCorrection, description: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-orange-400"
            />
          </div>
        </div>
        <button
          onClick={handleAddCorrection}
          disabled={!newCorrection.category || !newCorrection.description.trim()}
          className={`px-4 py-2 rounded font-medium transition-colors ${
            newCorrection.category && newCorrection.description.trim()
              ? 'bg-orange-400 text-black hover:bg-orange-300'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Add Correction
        </button>
      </div>

      {/* Progress Summary */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-lg font-semibold text-white mb-4">Improvement Progress</h4>
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-300">Completion Rate</span>
          <span className="text-orange-400 font-bold">
            {corrections.length === 0 ? '100%' : `${Math.round((completedCount / corrections.length) * 100)}%`}
          </span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div 
            className="bg-orange-400 h-2 rounded-full transition-all duration-300"
            style={{ width: corrections.length === 0 ? '100%' : `${(completedCount / corrections.length) * 100}%` }}
          ></div>
        </div>
        {highPriorityCount > 0 && (
          <p className="text-red-300 text-sm mt-2">
            ⚠️ {highPriorityCount} high-priority issue{highPriorityCount !== 1 ? 's' : ''} remaining
          </p>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Recommendations
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium bg-orange-400 text-black hover:bg-orange-300 transition-colors"
        >
          Continue to Final Review →
        </button>
      </div>
    </div>
  );
};